import React, { useState, useRef, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  CheckCircleIcon,
  X,
  AlertCircle,
  ArrowLeft,
  ArrowLeftIcon,
  Check,
  Info,
  BookOpen,
  Keyboard,
  Clock,
  Handshake,
  GripVertical,
} from 'lucide-react';
import ProblemDetailModal from '../../orgadmin/coding/ProblemDetailModal';
import CodingRoundConfig from '../../orgadmin/coding/CodingRoundConfig';
import host from '../../../global';
import { toast } from 'react-toastify';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useEffect } from 'react';
import AIProcessingLoader from '../../AIProcessingLoader';
import QuestionGenerationLoader from '../../QuestionGenerationLoader';
import Loader from '../../Loader';
import JobDescriptionLoader from '../../JobDescriptionLoader';
import { filterJobRoles } from '../jobRoles';
import LocationAutocomplete from '../../common/LocationAutocomplete';
import { useTheme } from '../../../hooks/useTheme';
import LimitExceededModal from '../../shared_components/LimitExceededModal';
import { useDispatch, useSelector } from 'react-redux';
// import Create_Jd_Commom_Step_One from './Jobs/Create_Jd_Commom_Step_One';
import SmartStep2_SelectSkills from './steps/smart/SmartStep2_SelectSkills';
import SharedStep1_JobDetails from './SharedStep1_JobDetails';
import SharedStep4_Review from './SharedStep4_Review';
//Gaurded Navigation from contexts
import { useNavigationGuard } from '../../../contexts/NavigationGuardContext';
import DiscardChangesModal from '../../shared/DiscardChangesModal';
import { toggleisjdcreated } from '../../../store/slices/miscSlice';
import { guideJobCreated } from '../../../store/slices/firstRunGuideSlice';
import HelpVideoModal from '../../shared_components/HelpVideoModal';
import { HELP_VIDEOS } from '../../../config/helpVideos';

const ItemType = 'ITEM';

const QUESTION_STYLE_MAP = {
  screening: 'Initial Screening',
  knowledge: 'Core Knowledge Evaluation',
  custom: 'Bring Your Own Questions',
};

const getInterviewTypeLabel = (styleId) => {
  return QUESTION_STYLE_MAP[styleId] || styleId;
};

const buildSkillTopicsMapping = (
  easy_skills,
  medium_skills,
  hard_skills,
  required_skills,
) => {
  const skillTopics = {};

  // Map Easy skills with their topics
  const easyTopics = required_skills.Easy || [];
  easy_skills.forEach((skill, idx) => {
    skillTopics[skill] = easyTopics[idx] || [];
  });

  // Map Medium skills with their topics
  const mediumTopics = required_skills.Medium || [];
  medium_skills.forEach((skill, idx) => {
    skillTopics[skill] = mediumTopics[idx] || [];
  });

  // Map Hard skills with their topics
  const hardTopics = required_skills.Hard || [];
  hard_skills.forEach((skill, idx) => {
    skillTopics[skill] = hardTopics[idx] || [];
  });

  return skillTopics;
};

const DraggableAssessmentItem = ({ id, index, moveItem, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'ASSESSMENT_ITEM',
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'ASSESSMENT_ITEM',
    hover: (item, monitor) => {
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;
      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`transition-all duration-200  ${
        isDragging ? 'opacity-50 scale-102' : 'opacity-100 scale-100'
      }`}
    >
      {children}
    </div>
  );
};

const experienceOptions = [
  { value: '0-0', label: '0 Years (Fresher)' },
  { value: '0-1', label: '0-1 Years (Entry Level)' },
  { value: '1-2', label: '1-2 Years (Junior)' },
  { value: '2-3', label: '2-3 Years (Mid-Junior)' },
  { value: '3-5', label: '3-5 Years (Mid-Level)' },
  { value: '5-7', label: '5-7 Years (Senior)' },
  { value: '7-10', label: '7-10 Years (Lead)' },
  { value: '10-15', label: '10-15 Years (Principal)' },
  { value: '15+', label: '15+ Years (Executive)' },
];

const CustomDropdown = ({
  label,
  options,
  value,
  onChange,
  required,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
        optionRefs.current = [];
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-scroll to highlighted option
  useEffect(() => {
    if (selectedIndex >= 0 && optionRefs.current[selectedIndex] && isOpen) {
      optionRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex, isOpen]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSelectedIndex(-1);
    optionRefs.current = [];
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setSelectedIndex(0);
        } else {
          setSelectedIndex((prev) =>
            prev < options.length - 1 ? prev + 1 : 0,
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : options.length - 1,
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && selectedIndex >= 0) {
          handleSelect(options[selectedIndex].value);
        } else if (!isOpen) {
          setIsOpen(true);
          setSelectedIndex(0);
        }
        break;
      case 'Escape':
        if (isOpen) {
          setIsOpen(false);
          setSelectedIndex(-1);
          optionRefs.current = [];
        }
        break;
    }
  };

  return (
    <div className="relative">
      <label
        className={`
    absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgPrimary px-1
  `}
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div ref={dropdownRef} className="w-full mt-2">
        <button
          type="button"
          className={`w-full px-6 py-3 bg-transparent border border-[#5A4479] rounded-full dark:text-[#CCCCCC] text-textSecondary text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-colors duration-200 ${className}`}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span>
            {options.find((opt) => opt.value === value)?.label ||
              'Select an option'}
          </span>
          <svg
            className="dark:text-[#5F6373] text-textSecondary h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
            />
          </svg>
        </button>
        {isOpen && (
          <ul
            className="absolute z-10 w-full mt-1 dark:bg-[#0a1733] bg-bgPrimary border border-[#5A4479] rounded-lg shadow-lg max-h-60 overflow-auto"
            role="listbox"
          >
            {options.map((option, index) => (
              <li
                key={option.value}
                ref={(el) => (optionRefs.current[index] = el)}
                className={`px-6 py-2 dark:text-[#CCCCCC] text-textSecondary cursor-pointer transition-colors duration-200 ${
                  index === selectedIndex
                    ? 'dark:bg-[#5A4479] dark:text-white text-textSecondary bg-gray-300'
                    : 'dark:hover:bg-[#2A2A2A] hover:bg-gray-300'
                }`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={value === option.value}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Static time options for interview duration selection
const SCREENING_TIME_OPTIONS = [
  { mins: 10, skills: 3, questions: 6 },
  { mins: 15, skills: 4, questions: 8 },
  { mins: 20, skills: 5, questions: 10 },
];

const KNOWLEDGE_TIME_OPTIONS = [30, 45, 60, 90];

// 3 skills→45min, 4-5→60min, 6+→90min
const getDefaultTimeForSkills = (skillCount) => {
  if (skillCount <= 3) return 45;
  if (skillCount <= 5) return 60;
  return 90; // 6 or more skills
};

// Helper function to calculate total skills from generatedJD
const getTotalSkillsCount = (generatedJD) => {
  const easyCount = generatedJD?.Required_Skills?.Easy?.length || 0;
  const mediumCount = generatedJD?.Required_Skills?.Medium?.length || 0;
  const hardCount = generatedJD?.Required_Skills?.Hard?.length || 0;
  return easyCount + mediumCount + hardCount;
};

function CreateJDSmartView() {
  const [experience, setExperience] = useState(0);
  const accessToken = sessionStorage.getItem('accessToken');
  const navigate = useNavigate();
  const location = useLocation();
  const editJdId = location.state?.jd_id || null;
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isHelpVideoOpen, setIsHelpVideoOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSkillsLoading, setSkillsLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const customRequiredSkillRef = useRef('');
  const [isAdminAuthorized, setIsAdminAuthorized] = useState(null);
  const [testPermissions, setTestPermissions] = useState({
    mcq_enabled: false,
    coding_enabled: false,
    behavioral_enabled: false,
    typing_enabled: false,
    candidate_form_enabled: false,
  });
  const [jDPermissions, setJDPermissions] = useState({
    create_jd_v1_enabled: false,
    create_jd_v1_remaining: 0,
  });
  const jobRoleInputRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode] = useState('edit');
  const editableRef = useRef(null);
  const dropdownRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');
  const timeLimitOptions = [15, 30, 45, 60];
  const CUSTOM_TIME_OPTIONS = [20, 40, 60];
  const [isSavingQuestions, setIsSavingQuestions] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [isGeneratingJD, setIsGeneratingJD] = useState(false);
  const [isUploadingJD, setIsUploadingJD] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [showJobRoleSuggestions, setShowJobRoleSuggestions] = useState(false);
  const [jobRoleSuggestions, setJobRoleSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const jobRoleContainerRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [currentSubStep, setCurrentSubStep] = useState(1);
  const suggestionRefs = useRef([]);
  const debounceTimeoutRef = useRef(null);
  const addSkillCooldownRef = useRef(false);
  const [isJDExpanded, setIsJDExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);

  // Coding round local state
  const [codingSetProblems, setCodingSetProblems] = useState([]);
  const [selfHostedProblemIds, setSelfHostedProblemIds] = useState(new Set());
  const [isFetchingSetProblems, setIsFetchingSetProblems] = useState(false);
  const [codingSetsData, setCodingSetsData] = useState({}); // set_name -> full set doc (question_slots, time_limit_minutes, allowed_languages)
  const isFirstCodingSetLoad = useRef(true);
  const [viewingProblem, setViewingProblem] = useState(null);
  const [viewingProblemLoading, setViewingProblemLoading] = useState(false);
  const [limitModal, setLimitModal] = useState({
    isOpen: false,
    featureKey: null,
    errorCode: null,
  });
  const org_id = useSelector((state) => state.userSlice.org_id);

  const dispatch = useDispatch();

  const selectableStyles = `
    /* Make every piece of text selectable */
    .selectable-report * {
      user-select: text !important;
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
    }
  `;


  const { theme } = useTheme();
  const isEditFlow = location.state?.isEditFlow || false;
  const getSteps = () => {
    const firstStepName = isEditFlow ? 'Edit a Job Role' : 'Create a Job Role';

    if (currentStep === 1 && currentSubStep === 1) {
      return [{ id: 1, displayNumber: 1, name: firstStepName }];
    }

    if (currentStep === 1 && currentSubStep === 2) {
      return [{ id: 1, displayNumber: 1, name: firstStepName }];
    }

    if (selectedStyle === 'screening' || selectedStyle === 'custom') {
      return [
        { id: 1, displayNumber: 1, name: firstStepName },
        { id: 3, displayNumber: 2, name: 'Configurations' },
        { id: 4, displayNumber: 3, name: 'Review' },
      ];
    } else if (selectedStyle === 'knowledge') {
      return [
        { id: 1, displayNumber: 1, name: firstStepName },
        { id: 2, displayNumber: 2, name: 'Select Skills' },
        { id: 3, displayNumber: 3, name: 'Configurations' },
        { id: 4, displayNumber: 4, name: 'Review' },
      ];
    }

    return [{ id: 1, displayNumber: 1, name: firstStepName }];
  };

  const inputLabelStyles = `
    absolute text-sm font-normal text-[#DAC0FF] -top-1 left-6 bg-[#140F1C] px-1
  `;

  // Handle clicking outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (currentStep === 1 && jobRoleInputRef.current) {
      jobRoleInputRef.current.focus();
    }
  }, [currentStep]);

  const [sections, setSections] = useState(() => {
    const savedSections = localStorage.getItem('createJDSections');
    return savedSections
      ? JSON.parse(savedSections)
      : {
          sourcing: {
            isExpanded: false,
            isChecked: false,
            content:
              'Configure your candidate sourcing preferences, channels, and automation settings.',
          },
          screening: {
            isExpanded: false,
            isChecked: false,
            content:
              'Set up your screening criteria, questionnaires, and filtering parameters.',
          },
          assessment: {
            isExpanded: true,
            isChecked: true,
            content:
              'Customize your assessment methods, scoring criteria, and evaluation process.',
          },
        };
  });

  useEffect(() => {
    localStorage.setItem('createJDSections', JSON.stringify(sections));
  }, [sections]);

  const candidateRanges = [
    { value: '5', label: '5 candidates' },
    { value: '25', label: '25 candidates' },
    { value: '50', label: '50 candidates' },
    { value: '75', label: '75 candidates' },
    { value: '100', label: '100 candidates' },
    { value: '150', label: '150 candidates' },
    { value: '200', label: '200 candidates' },
    { value: '400', label: '400 candidates' },
  ];

  const totalSteps =
    selectedStyle === 'screening' || selectedStyle === 'custom'
      ? 3
      : selectedStyle === 'knowledge'
        ? 4
        : 1;

  const toggleExpandedSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem('createJDFormData');
    const savedSections = localStorage.getItem('createJDSections');
    const parsedSections = savedSections ? JSON.parse(savedSections) : null;

    const initialFormData = savedData
      ? JSON.parse(savedData)
      : {
          jobRole: '',
          experience: '',
          location: '',
          responsibilities: '',
          requiredSkills: [],
          optionalSkills: [],
          aiSuggestedSkills: [],
          generatedJD: {},
          interview_type: '',
          role_summary: '',
          question_design_context: '',
          rawJD: {},
          isAnalyzed: false,
          isFineTuned: false,
          customRequiredSkill: '',
          customOptionalSkill: '',
          showInterviewConfig: false,
          interview_timelimit: null,
          isInterviewTimeManuallySet: false,
          isMCQIncluded: false,
          isCodingIncluded: false,
          isTypingIncluded: false,
          isBehavioralIncluded: false,
          isCandidateFormIncluded: false,
          CodingTimeLimit: 10,
          CodingJudgingCriteria: '1',
          codingQuestionConfig: [],
          typingTimeLimit: 1,
          selectedSet: '',
          selectedCodingSet: '',
          selectedBehavioralSet: '',
          sets: [],
          codingSets: [],
          behavioralSets: [],
          isCustomQuestionsIncluded: false,
          customQuestions: '',
          selectedServices: {
            sourcing: false,
            screening: false,
            assessment: true,
          },
          editedQuestions: [],
          questions: [],
          candidatesRequired: 5,
          searchLocations: [],
          assessmentSequence: ['interview'],
          companyName: '',
          companyDescription: '',
          jobType: 'fulltime',
          workLocationType: 'office',
          workLocationInput: '',
          workLocations: [],
          noticePeriod: 0,
          noticeStrictness: 'strict',
          maxCTC: '',
          ctcStrictness: 'strict',
          errors: {
            jobRole: '',
            minExp: '',
            maxExp: '',
            requiredSkills: '',
            interviewTimeLimit: '',
            selectedSet: '',
            selectedCodingSet: '',
            typingTimeLimit: '',
            selectedBehavioralSet: '',
          },
          sequence: [
            {
              id: 'interview',
              label: 'Technical Interview',
              mandatory: true,
              icon: '👥',
            },
          ],
          interview_type: '',
          role_summary: '',
          question_design_context: '',
          sampleQuestions: {},
          Topics_To_Ask: {},
          preGeneratedTopics: {},
          isMagicBoxComplete: false,
          originalAISkills: [],
        };

    if (parsedSections) {
      initialFormData.selectedServices = {
        sourcing: parsedSections.sourcing?.isChecked || false,
        screening: parsedSections.screening?.isChecked || false,
        assessment: parsedSections.assessment?.isChecked ?? true,
      };
    }

    return initialFormData;
  });
  
  //Useguard from context to prevent navigation when there are unsaved changes
  const { enableGuard, disableGuard, isBlocked, proceed, reset } = useNavigationGuard(); 
  const isFormDirty =
      !!formData.jobRole.trim() ||
      !!formData.responsibilities.trim() ||
      (formData.requiredSkills?.length ?? 0) > 0 ||
      currentStep > 1;

  useEffect(() => {
    if (isFormDirty) {
        enableGuard();
    } else {
        disableGuard();
      }
    return () => disableGuard();
    }, [isFormDirty, enableGuard, disableGuard]);

  // If jd_id is present (coming from ViewCreatedJDs), fetch JD data and pre-fill
  useEffect(() => {
    if (!editJdId) {
      // Clear local storage if not editing, so a new form is fresh
      localStorage.removeItem('createJDFormData');
      localStorage.removeItem('createJDSections');
      return;
    }

    const fetchJDForEdit = async () => {
      setIsLoading(true); // Start loading
      try {
        const response = await axios.get(
          `${host}/get_jd_for_edit/${editJdId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        const data = response.data;
        console.log('Pre-filling JD data for editing:', data);

        const description = data.description || {};
        const requiredSkillsFromDB = description.Raw_Required_Skills || [];
        const generatedJDFromDB = {
          ...description,
          job_description: description.Raw_Job_Description || '',
        };

        const assessmentSequence = data.assessment_sequence || [];
        const sequence = assessmentSequence
          .map((id) => {
            switch (id) {
              case 'interview':
                return {
                  id: 'interview',
                  label: 'Technical Interview',
                  mandatory: true,
                  icon: '👥',
                };
              case 'mcq':
                return {
                  id: 'mcq',
                  label: 'Knowledge Test',
                  mandatory: false,
                  icon: '📝',
                };
              case 'coding':
                return {
                  id: 'coding',
                  label: 'Coding Test',
                  mandatory: false,
                  icon: '💻',
                };
              case 'typing':
                return {
                  id: 'typing',
                  label: 'Typing Test',
                  mandatory: false,
                  icon: '⌨️',
                };
              case 'behavioral':
                return {
                  id: 'behavioral',
                  label: 'Behavioural Assessment',
                  mandatory: false,
                  icon: '🤝',
                };
              default:
                return null;
            }
          })
          .filter(Boolean);

        const questionType = data.questionType || 'knowledge';
        const selectedStyleValue =
          questionType === 'Initial Screening' ? 'screening' :
          questionType === 'Bring Your Own Questions' ? 'custom' : 'knowledge';
        setSelectedStyle(selectedStyleValue);

        const experienceValue =
          description.minExp === 15 && description.maxExp === 40
            ? '15+'
            : `${description.minExp ?? 0}-${description.maxExp ?? 1}`;

        setFormData((prev) => ({
          ...prev,
          jd_id: data._id,
          jobRole: data.jobrole || '',
          minExp: description.minExp ?? 0,
          maxExp: description.maxExp ?? 1,
          experience: experienceValue,
          location: data.location || '',
          responsibilities: description.Raw_Job_Description || '',

          // Step 2
          requiredSkills: requiredSkillsFromDB,
          optionalSkills: description.GoodtoHave || [],
          aiSuggestedSkills: [], // AI suggestions are transient, don't prefill
          originalAISkills: requiredSkillsFromDB, // Assume all came from AI for simplicity
          generatedJD: generatedJDFromDB,
          isAnalyzed: true,
          isFineTuned: true,

          // Step 3
          interview_timelimit: data.interview_timelimit || 30,
          isInterviewTimeManuallySet: true, // Mark as manually set to preserve value

          isMCQIncluded: !!data.mcq_set_id && data.mcq_time_limit > 0,
          selectedSet: data.mcq_set_id || '',

          isCodingIncluded:
            data.Coding_set !== 'NA' && data.coding_time_limit > 0,
          selectedCodingSet: data.Coding_set || '',
          CodingTimeLimit: data.coding_time_limit || 10,
          CodingJudgingCriteria: data.Coding_Judge_Criteria || '1',
          codingQuestionConfig: data.coding_question_config || [],

          isTypingIncluded: data.typing_time_limit > 0,
          typingTimeLimit: data.typing_time_limit || 1,

          isBehavioralIncluded:
            !!data.selected_behavioral_set && data.selected_behavioral_set !== 'NA',
          selectedBehavioralSet:
            data.selected_behavioral_set && data.selected_behavioral_set !== 'NA'
              ? data.selected_behavioral_set
              : '',

          isCustomQuestionsIncluded: (data.customQuestions || []).length > 0,
          customQuestions: (data.customQuestions || []).join('\\n'),
          questions: data.customQuestions || [],
          editedQuestions: data.customQuestions || [],

          assessmentSequence: assessmentSequence,
          sequence: sequence,

          // Step 4 (Review)
          Pre_Written_Questions: description.Pre_Written_Questions || {},
          Topics_To_Ask: description.Topics_To_Ask || {},
          preGeneratedTopics: description.Required_Skills || {
            Easy: [],
            Medium: [],
            Hard: [],
          },

          // Other fields
          selectedServices: data.selectedServices || {
            sourcing: false,
            screening: false,
            assessment: true,
          },
          errors: {
            jobRole: '',
            minExp: '',
            maxExp: '',
            requiredSkills: '',
            interviewTimeLimit: '',
            selectedSet: '',
            selectedCodingSet: '',
            typingTimeLimit: '',
            selectedBehavioralSet: '',
          },
          jd_id: data._id,
        }));

        // Set initial state for sections based on loaded data
        setSections({
          sourcing: {
            isExpanded: data.selectedServices?.sourcing,
            isChecked: data.selectedServices?.sourcing,
            content: '...',
          },
          screening: {
            isExpanded: data.selectedServices?.screening,
            isChecked: data.selectedServices?.screening,
            content: '...',
          },
          assessment: {
            isExpanded: data.selectedServices?.assessment ?? true,
            isChecked: data.selectedServices?.assessment ?? true,
            content: '...',
          },
        });

        setCurrentStep(1);
        setCompletedSteps(new Set([1, 2, 3]));
      } catch (error) {
        console.error('Error fetching JD for edit:', error);
        toast.error('An error occurred while loading the job description.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchJDForEdit();
  }, [editJdId, accessToken]);

  useEffect(() => {
    setCompletedSteps((prev) => {
      const newCompleted = new Set();

      const steps = getSteps();
      const validStepIds = new Set(steps.map((s) => s.id));

      for (let i = 1; i < currentStep; i++) {
        if (validStepIds.has(i)) {
          newCompleted.add(i);
        }
      }
      return newCompleted;
    });
  }, [currentStep, selectedStyle]);

  useEffect(() => {
    const skillKeys = Object.keys(formData.Pre_Written_Questions || {});

    if (skillKeys.length > 0) {
      setExpandedSections((prev) => ({
        ...prev,
        [skillKeys[0]]: true,
      }));
    }
  }, [formData.Pre_Written_Questions]);

  useEffect(() => {
    if (
      (currentStep === 4 && selectedStyle === 'screening') ||
      (selectedStyle === 'custom' &&
        (!formData.questions || formData.questions.length === 0))
    ) {
      setIsJDExpanded(true);
    } else if (currentStep === 4 && selectedStyle !== 'screening') {
      setIsJDExpanded(false);
    }
  }, [currentStep, selectedStyle, formData.questions]);

  const handleJobRoleChangeWithSuggestions = useCallback(
    (e) => {
      if (!e || !e.target) return;

      let value = e.target.value || '';
      value = value.replace(/[^a-zA-Z0-9 ]/g, '');

      if (/^\d+$/.test(value)) {
        updateFormData({ jobRole: '' });
        setJobRoleSuggestions([]);
        setShowJobRoleSuggestions(false);
        return;
      }

      updateFormData({
        jobRole: value,
        errors: {
          ...formData.errors,
          jobRole: value.trim() === '' ? ' ' : '',
        },
      });

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (value.length >= 2) {
          let suggestions = filterJobRoles(value);
          suggestions = Array.from(new Set(suggestions));

          setJobRoleSuggestions(suggestions);
          setShowJobRoleSuggestions(suggestions.length > 0);
          setSelectedSuggestionIndex(-1);
        } else {
          setJobRoleSuggestions([]);
          setShowJobRoleSuggestions(false);
        }
      }, 150);
    },
    [formData.errors],
  );

  const handleSuggestionSelect = useCallback((role) => {
    updateFormData({ jobRole: role });
    setJobRoleSuggestions([]);
    setShowJobRoleSuggestions(false);
    setFormData((prev) => ({
      ...prev,
      responsibilities: "",
      maxExp:"",
      minExp:""
    }));
  }, []);

  const handleJobRoleKeyDown = useCallback(
    (e) => {
      if (!showJobRoleSuggestions || jobRoleSuggestions.length === 0) {
        if (e.key === 'Tab' || e.key === 'Enter') {
          setShowJobRoleSuggestions(false);
        }
        return;
      }

      const { key } = e;
      const maxIndex = jobRoleSuggestions.length - 1;

      switch (key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev < maxIndex ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev > 0 ? prev - 1 : maxIndex,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedSuggestionIndex >= 0) {
            handleSuggestionSelect(jobRoleSuggestions[selectedSuggestionIndex]);
          } else {
            setShowJobRoleSuggestions(false);
            setSelectedSuggestionIndex(-1);
          }
          break;
        case 'Tab':
          setShowJobRoleSuggestions(false);
          break;
        case 'Escape':
          e.preventDefault();
          setShowJobRoleSuggestions(false);
          break;
      }
    },
    [
      showJobRoleSuggestions,
      jobRoleSuggestions,
      selectedSuggestionIndex,
      handleSuggestionSelect,
    ],
  );

  useEffect(() => {
    const index = selectedSuggestionIndex;
    const el = suggestionRefs.current[index];

    if (el) {
      el.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedSuggestionIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        jobRoleContainerRef.current &&
        !jobRoleContainerRef.current.contains(event.target)
      ) {
        setShowJobRoleSuggestions(false);
      }
    };

    if (showJobRoleSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showJobRoleSuggestions]);

  const handleExperienceSelect = (value) => {
    if (!value) return;
    const [min, max] = value.includes('+')
      ? [parseInt(value.replace('+', '')), 40]
      : value.split('-').map((v) => parseInt(v));

    updateFormData({
      minExp: min,
      maxExp: max,
      errors: {
        ...formData.errors,
        minExp: '',
        maxExp: '',
        experience: '',
      },
    });
    handleExperienceChange(min, max);
  };

  const onAddCustomSkill = () => {
    if (addSkillCooldownRef.current) return;

    const val = customRequiredSkillRef.current.value.trim();
    if (!val) return;

    // Case-insensitive duplicate check in requiredSkills
    const alreadyInRequired = formData.requiredSkills.some(
      (s) => s.toLowerCase() === val.toLowerCase()
    );
    if (alreadyInRequired) {
      addSkillCooldownRef.current = true;
      toast.error('This skill has already been added');
      customRequiredSkillRef.current.value = '';
      setTimeout(() => { addSkillCooldownRef.current = false; }, 1000);
      return;
    }

    if (formData.requiredSkills.length >= 6) {
      addSkillCooldownRef.current = true;
      toast.error('You can only add a maximum of 6 skills');
      setTimeout(() => { addSkillCooldownRef.current = false; }, 1000);
      return;
    }

    // Find case-insensitive match in aiSuggestedSkills
    const matchedSuggested = formData.aiSuggestedSkills.find(
      (s) => s.toLowerCase() === val.toLowerCase()
    );

    // Use the canonical name from suggestions if matched, else typed value
    const skillToAdd = matchedSuggested ?? val;

    const experience = (parseInt(formData.minExp) + parseInt(formData.maxExp)) / 2;
    let updatedJD = formData.generatedJD ? { ...formData.generatedJD } : {};
    if (!updatedJD.Required_Skills) {
      updatedJD.Required_Skills = { Hard: [], Medium: [], Easy: [] };
    } else {
      updatedJD.Required_Skills = {
        Hard: [...(updatedJD.Required_Skills.Hard || [])],
        Medium: [...(updatedJD.Required_Skills.Medium || [])],
        Easy: [...(updatedJD.Required_Skills.Easy || [])],
      };
    }

    if (experience > 4) {
      updatedJD.Required_Skills.Hard.push(skillToAdd);
    } else {
      updatedJD.Required_Skills.Medium.push(skillToAdd);
    }

    addSkillCooldownRef.current = true;

    updateFormData({
      requiredSkills: [...formData.requiredSkills, skillToAdd],
      // ✅ Remove from left side if it existed there (case-insensitive)
      aiSuggestedSkills: matchedSuggested
        ? formData.aiSuggestedSkills.filter(
            (s) => s.toLowerCase() !== val.toLowerCase()
          )
        : formData.aiSuggestedSkills,
      generatedJD: updatedJD,
      errors: { ...formData.errors, requiredSkills: '' },
    });

    customRequiredSkillRef.current.value = '';
    setTimeout(() => { addSkillCooldownRef.current = false; }, 300);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['.pdf', '.docx', '.doc', '.txt'];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      toast.error('Please upload PDF, DOCX, DOC, or TXT files only');
      return;
    }

    setIsUploadingJD(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${host}/extract-jd-text`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: formData,
      });

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.detail || 'Failed to extract text');

      updateFormData({
        responsibilities: result.extracted_text || '',
      });
      // toast.success("JD uploaded and text extracted successfully!");
    } catch (error) {
      console.error('Error extracting JD text:', error);
      toast.error('Failed to extract text from JD. Please try again.');
    } finally {
      setIsUploadingJD(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploadingJD) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDropJD = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const toggleDropdown = () => {
    if (!isEditing) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const selectTimeLimit = (mins) => {
    updateFormData({
      interview_timelimit: mins,
      isInterviewTimeManuallySet: false,
    });
    setIsDropdownOpen(false);
  };

  const startEditing = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    setIsDropdownOpen(false);

    setTimeout(() => {
      if (editableRef.current) {
        editableRef.current.focus();
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 0);
  };

  const generateSampleQuestions = async () => {
    console.log('Starting generateSampleQuestions');
    console.log('selectedStyle:', selectedStyle);
    console.log('skills:', [
      ...formData.requiredSkills,
      ...formData.optionalSkills,
    ]);

    setIsGeneratingQuestions(true);
    try {
      const allSkills = [
        ...formData.requiredSkills,
        ...formData.optionalSkills,
      ];

      const requiredSkills = {
        Easy: formData.generatedJD?.Required_Skills?.Easy || [],
        Medium: formData.generatedJD?.Required_Skills?.Medium || [],
        Hard: formData.generatedJD?.Required_Skills?.Hard || [],
      };

      // Build raw job description for topic generation
      const rolesAndResponsibilities =
        formData.generatedJD?.['Roles & Responsibilities'] || [];
      const rawJobDescription = Array.isArray(rolesAndResponsibilities)
        ? rolesAndResponsibilities.join('\n')
        : rolesAndResponsibilities;

      console.log('Step 1: Generating skill topics...');
      const topicsResponse = await axios.post(
        `${host}/generate-skill-topics`,
        {
          jobrole: formData.jobRole,
          minExp: formData.minExp,
          maxExp: formData.maxExp,
          rawJobDescription: rawJobDescription,
          Required_Skills: requiredSkills,
          interview_timelimit: formData.interview_timelimit,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const topicsResult = topicsResponse.data;
      console.log('Topics Response:', topicsResult);
      // {skill: [topic1, topic2, topic3]}
      const skillTopics = buildSkillTopicsMapping(
        requiredSkills.Easy || [],
        requiredSkills.Medium || [],
        requiredSkills.Hard || [],
        topicsResult.Required_Skills,
      );
      const preGeneratedTopics = topicsResult.Required_Skills; // {Easy: [[...]], Medium: [[...]], Hard: [[...]]}

      console.log('Generated skillTopics:', skillTopics);
      console.log('Generated preGeneratedTopics:', preGeneratedTopics);

      // STEP 2: Generate sample questions using those specific topics
      console.log('Step 2: Generating sample questions for specific topics...');
      const response = await fetch(`${host}/generate-sample-questions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          jobrole: formData.jobRole,
          minExp: formData.minExp,
          maxExp: formData.maxExp,
          questionType: getInterviewTypeLabel(selectedStyle),
          skills: allSkills,
          skillTopics: skillTopics,
        }),
      });

      const result = await response.json();
      console.log('Sample Questions Response:', result);

      if (!response.ok) throw new Error(result.detail || 'Failed');

      console.log(
        'Pre_Written_Questions from API:',
        result.Pre_Written_Questions,
      );
      console.log('Topics_To_Ask from API:', result.Topics_To_Ask);

      // A skill keeps its saved questions when they hold recruiter edits or still cover the
      // new per-skill budget (the generated count) — otherwise removing skills would leave
      // the interview short. Everything else takes the generated ones; removed skills drop out.
      const savedQuestions = formData.Pre_Written_Questions || {};
      const savedTopics = formData.Topics_To_Ask || {};
      const preWrittenQuestions = { ...result.Pre_Written_Questions };
      const topicsToAsk = { ...result.Topics_To_Ask };
      const keptSkills = new Set(
        Object.keys(preWrittenQuestions).filter((skill) => {
          const saved = savedQuestions[skill];
          return (
            saved?.length > 0 &&
            (saved.some((q) => q.edited) || saved.length >= preWrittenQuestions[skill].length)
          );
        }),
      );
      keptSkills.forEach((skill) => {
        preWrittenQuestions[skill] = savedQuestions[skill];
        if (savedTopics[skill]) topicsToAsk[skill] = savedTopics[skill];
        else delete topicsToAsk[skill];
      });
      const topicsFor = (level) =>
        (requiredSkills[level] || []).map((skill, i) =>
          keptSkills.has(skill)
            ? savedQuestions[skill].map((q) => q.topic)
            : preGeneratedTopics?.[level]?.[i] || [],
        );

      updateFormData({
        Pre_Written_Questions: preWrittenQuestions,
        Topics_To_Ask: topicsToAsk,
        preGeneratedTopics: {
          Easy: topicsFor('Easy'),
          Medium: topicsFor('Medium'),
          Hard: topicsFor('Hard'),
        },
      });
      return true;
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error('Failed to generate questions');
      return false;
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  useEffect(() => {
    const checkAdminAuthorization = async () => {
      try {
        const response = await axios.get(`${host}/check_permissions_for_jd`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setIsAdminAuthorized(response.data.is_authorized);
        console.log(response.data);
        setTestPermissions({
          mcq_enabled: response.data.mcq_enabled || false,
          coding_enabled: response.data.coding_enabled || false,
          behavioral_enabled: response.data.behavioral_enabled || false,
          typing_enabled: response.data.typing_enabled || false,
          candidate_form_enabled: response.data.candidate_form_enabled || false,
        });
        setJDPermissions({
          create_jd_v1_enabled: response.data.create_jd_v1_enabled || false,
          create_jd_v1_remaining: response.data.create_jd_v1_remaining || 0,
        });

        console.log('Admin authorization status:', response.data.is_authorized);
        console.log('Test permissions:', {
          mcq: response.data.mcq_enabled,
          coding: response.data.coding_enabled,
          typing: response.data.typing_enabled,
          form: response.data.candidate_form_enabled,
        });
      } catch (error) {
        console.error('Error checking admin ID:', error);
        if (error.response) {
          const { status, data } = error.response;
          if (status === 401) {
            toast.error('Unauthorized: Invalid token');
            sessionStorage.removeItem('accessToken');
          } else {
            toast.error(
              `Error: ${data.detail || 'Failed to check admin status'}`,
            );
          }
        } else {
          toast.error('Network error: Unable to reach the server');
        }
        setIsAdminAuthorized(false);
        setTestPermissions({
          mcq_enabled: false,
          coding_enabled: false,
          behavioral_enabled: false,
          typing_enabled: false,
          candidate_form_enabled: false,
        });
      }
    };

    if (accessToken) {
      checkAdminAuthorization();
    } else {
      setIsAdminAuthorized(false);
      setTestPermissions({
        mcq_enabled: false,
        coding_enabled: false,
        behavioral_enabled: false,
        typing_enabled: false,
        candidate_form_enabled: false,
      });
    }
  }, [accessToken]);

  useEffect(() => {
    localStorage.setItem('createJDFormData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    return () => {
      localStorage.removeItem('createJDFormData');
      localStorage.removeItem('createJDSections');
      console.log('localStorage cleared on CreateJD unmount');
    };
  }, []);

  useEffect(() => {
    const tests = [];

    if (formData.isMCQIncluded) {
      tests.push({ id: 'mcq', label: 'Knowledge Test', mandatory: false });
    }
    if (formData.isCodingIncluded) {
      tests.push({ id: 'coding', label: 'Coding Test', mandatory: false });
    }
    if (formData.isTypingIncluded) {
      tests.push({ id: 'typing', label: 'Typing Test', mandatory: false });
    }
    if (formData.isBehavioralIncluded) {
      tests.push({ id: 'behavioral', label: 'Behavioural Assessment', mandatory: false });
    }

    let newSequence = [
      { id: 'interview', label: 'Technical Interview', mandatory: true },
    ];

    if (formData.sequence && formData.sequence.length > 0) {
      const existingTests = formData.sequence.filter(
        (item) => item.id !== 'interview',
      );

      if (existingTests.length === tests.length) {
        newSequence = [newSequence[0], ...existingTests];
      } else {
        newSequence.push(...tests);
      }
    } else {
      newSequence.push(...tests);
    }

    updateFormData({
      sequence: newSequence,
      assessmentSequence: newSequence.map((item) => item.id),
    });
  }, [
    formData.isMCQIncluded,
    formData.isCodingIncluded,
    formData.isTypingIncluded,
    formData.isBehavioralIncluded,
  ]);

  const moveItem = (fromIndex, toIndex) => {
    const newSequence = [...formData.sequence];
    const [movedItem] = newSequence.splice(fromIndex, 1);
    newSequence.splice(toIndex, 0, movedItem);
    updateFormData({
      sequence: newSequence,
      assessmentSequence: newSequence.map((item) => item.id),
    });
  };

  const handleWorkLocationInputChange = (e) => {
    updateFormData({ workLocationInput: e.target.value });
  };

  const handleWorkLocationAdd = (e) => {
    if (e.key === 'Enter' && formData.workLocationInput.trim()) {
      updateFormData({
        workLocations: [
          ...formData.workLocations,
          formData.workLocationInput.trim(),
        ],
        workLocationInput: '',
      });
    }
  };

  const handleWorkLocationRemove = (location) => {
    updateFormData({
      workLocations: formData.workLocations.filter((loc) => loc !== location),
    });
  };

  const NavigationButtons = ({
    onBack,
    onNext,
    nextLabel = 'Next',
    showBackArrow = true,
    nextDisabled = false,
    showBackButton = true,
    showborderbtn = false,
  }) => {
    return (
      <div className="flex justify-between items-center w-full py-4">
        {showBackButton ? (
          <button
            onClick={onBack}
            className={`${showborderbtn && theme !== 'dark' ? 'border-btn !text-textSecondary' : ' button '} flex items-center gap-2   rounded-full dark:!text-white dark:border dark:border-gray-600 hover:border-gray-400 transition-all duration-200`}
          >
            <span className="flex flex-row">
              {/* {showBackArrow && <ArrowLeftIcon className="w-5 h-5" />} */}
              {showBackButton && 'Back'}
            </span>
          </button>
        ) : (
          <div></div>
        )}
        <button
          onClick={onNext}
          disabled={nextDisabled}
          className={`button px-6 py-3 rounded-full text-white transition-all duration-200 active:scale-95 ${
            nextDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
          }`}
        >
          <span>{nextLabel}</span>
        </button>
      </div>
    );
  };

  // Update the renderProgressBar function - replace the entire progress bar width calculation
  const renderProgressBar = () => {
    const steps = getSteps();

    // Calculate progress width based on current state
    const calculateProgressWidth = () => {
      // If on step 1, substep 1 (initial form)
      if (currentStep === 1 && currentSubStep === 1) {
        return 6;
      }

      // If on step 1, substep 2 (magic box) - use a small percentage that works across screen sizes
      if (currentStep === 1 && currentSubStep === 2) {
        return 12; // Smaller percentage works better responsively
      }

      // For screening path (3 total steps)
      if (selectedStyle === 'screening' || selectedStyle === 'custom') {
        const stepMap = {
          1: 12, // Still on create (magic box done)
          3: 55, // Configurations (step 2 of 3)
          4: 95, // Review completed
        };
        return stepMap[currentStep] || 0;
      }

      // For knowledge path (4 total steps)
      if (selectedStyle === 'knowledge') {
        const stepMap = {
          1: 12, // Still on create (magic box done)
          2: 41, // Select Skills (step 2 of 4)
          3: 68, // Configurations (step 3 of 4)
          4: 95, // Review completed
        };
        return stepMap[currentStep] || 0;
      }

      return 0;
    };

    return (
      <div className="w-full px-4 py-8">
        <div className="px-12 mx-auto">
          <div className="flex items-center justify-between mx-10">
            {steps.map((step, index) => {
              const isCompleted = completedSteps.has(step.id);
              const isCurrent = currentStep === step.id;
              const isActive = isCurrent || isCompleted;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex items-center gap-4">
                    <div
                      className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors duration-200
                      ${
                        isActive
                          ? 'border-[#069855] text-[#069855]'
                          : 'border-[#575B72] text-[#575B72] bg-transparent'
                      }
                    `}
                    >
                      {step.displayNumber}
                    </div>

                    <div className="min-w-0">
                      <div
                        className={`text-base transition-colors duration-200 ${
                          isActive && isCompleted
                            ? 'text-[#069855]'
                            : isActive
                              ? 'dark:text-white text-textPrimary'
                              : 'text-[#575B72]'
                        }`}
                      >
                        {step.name}
                      </div>
                    </div>
                  </div>

                  {index < steps.length - 1 && (
                    <div className="flex items-center flex-shrink-0">
                      <span
                        className={`text-gray-600 h-6 w-6 transition-colors duration-200 ${
                          isCompleted ? 'text-[#069855]' : 'text-[#575B72]'
                        }`}
                      >
                        {`>>`}
                      </span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="w-full bg-gray-700 h-0.5 rounded-full mt-6">
            <div
              className="bg-gradient-to-r from-pink-500 to-blue-500 h-0.5 rounded-full transition-all duration-300"
              style={{
                width: `${calculateProgressWidth()}%`,
              }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  const toggleSection = (sectionKey) => {
    if (!sections[sectionKey].isChecked) return;

    setSections((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        isExpanded: !prev[sectionKey].isExpanded,
      },
    }));
  };

  const handleCheckboxChange = (sectionKey) => {
    setSections((prev) => {
      const updatedSections = {
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          isChecked: !prev[sectionKey].isChecked,
          isExpanded: !prev[sectionKey].isChecked,
        },
      };
      // Sync with formData.selectedServices
      updateFormData({
        selectedServices: {
          ...formData.selectedServices,
          [sectionKey]: !prev[sectionKey].isChecked,
        },
      });
      return updatedSections;
    });
  };

  const DraggableItem = ({ item, sourceBoxId }) => {
    const [{ isDragging }, drag] = useDrag({
      type: ItemType,
      item: { item, sourceBoxId },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const truncatedText =
      item.length > 50 ? item.substring(0, 50) + '....' : item;

    return (
      <div
        ref={drag}
        className={`${
          sourceBoxId === 'required'
            ? ` ${theme === 'dark' ? 'secondary-button' : 'secondary-button-light'}`
            : 'gradient-transparent-button'
        } cursor-pointer py-2 px-3 dark:text-white text-textSecondary rounded-full text-[13px] flex items-center space-x-2 transition-all duration-300 ${
          isDragging ? 'opacity-50 scale-95' : 'opacity-100'
        }`}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        title={item}
      >
        <span
          className={`dark:text-white ${sourceBoxId != 'aiSuggestedSkills' ? 'text-white' : 'text-textSecondary'} z-10`}
        >
          {truncatedText}
        </span>
        {sourceBoxId !== 'aiSuggestedSkills' && (
          <button
            onClick={(e) => (
              e.stopPropagation(),
              deleteItem(item, sourceBoxId)
            )}
            className="ml-2 group relative"
          >
            <div className="absolute inset-0 rounded-full opacity-0 transition-opacity"></div>
            <X
              className={`w-4 h-4 relative z-10 ${sourceBoxId != 'aiSuggestedSkills' ? 'text-gray-200' : 'text-textSecondary'} rounded-xl transition-transform`}
              strokeWidth={2.5}
            />
          </button>
        )}
      </div>
    );
  };

  const DraggableBox = ({
    id,
    name,
    items,
    handleDrop,
    bgColor = 'bg-blue-50',
    textColor = 'text-pink-500',
    icon: Icon = CheckCircleIcon,
    addSkillInput = null,
  }) => {
    const [{ canDrop }, drop] = useDrop({
      accept: ItemType,
      drop: (draggedItem, monitor) =>
        id !== 'aiSuggestedSkills' &&
        handleDrop(
          draggedItem,
          id,
          calculateTargetIndex(monitor.getClientOffset()),
        ),
      canDrop: () => id !== 'aiSuggestedSkills',
    });

    const calculateTargetIndex = (clientOffset) => {
      if (!clientOffset) return items.length;
      const bounds = document.getElementById(id).getBoundingClientRect();
      const offsetY = clientOffset.y - bounds.top;
      return items.reduce(
        (acc, _, i) => (offsetY <= 30 * (i + 1) ? acc : i + 1),
        0,
      );
    };

    return (
      <div className="relative">
        <label
          className={`absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgSecondary px-1`}
        >
          {name}
        </label>
        <div
          id={id}
          ref={drop}
          className={`w-full px-4 pb-4 pt-5 mt-2 bg-transparent border rounded-lg transition-all duration-300 ${
            canDrop
              ? 'border-purple-400 focus:ring-2 focus:ring-purple-400 focus:border-transparent'
              : 'border-[#5A4479] focus:ring-2 focus:ring-[#5A4479]'
          }`}
          style={{ height: '420px' }}
        >
          <div className="flex flex-col h-full">
            {/* Scrollable skills area */}
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 && name !== 'AI Suggested Skills' ? (
                <p className="text-2xl dark:font-medium font-normal text-center mt-36 dark:text-gray-500 text-gray-300 ml-2 text-opacity-60">
                  You can drop the skills here
                </p>
              ) : (
                <ul className="flex flex-wrap gap-[5px]">
                  {items.map((item, index) => (
                    <li key={index} className="flex-shrink-0 p-[1px]">
                      <DraggableItem
                        item={item}
                        index={index}
                        sourceBoxId={id}
                        deleteItem={deleteItem}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Fixed input at bottom */}
            {addSkillInput && (
              <div className="mt-2 flex gap-2 items-center flex-shrink-0">
                {addSkillInput}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const deleteItem = (item, sourceBoxId) => {
    if (sourceBoxId === 'required') {
      // Check if this skill was part of the ORIGINAL AI suggestions
      const wasOriginallyAISuggested = formData.originalAISkills.includes(item);

      updateFormData({
        requiredSkills: formData.requiredSkills.filter(
          (skill) => skill !== item,
        ),
        // Only put it back into AI box if it came from AI originally
        aiSuggestedSkills: wasOriginallyAISuggested
          ? Array.from(new Set([...formData.aiSuggestedSkills, item])) // prevents duplicates
          : formData.aiSuggestedSkills,
      });
    } else if (sourceBoxId === 'optional') {
      // If you ever add delete from optional, handle it here
      updateFormData({
        optionalSkills: formData.optionalSkills.filter(
          (skill) => skill !== item,
        ),
      });
    }
  };

  useEffect(() => {
    const fetchCombinedData = async () => {
      try {
        const response = await axios.get(`${host}/orgadmin/get_jd_data`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const data = response.data;
        updateFormData({
          sets: Array.isArray(data.set_names) ? data.set_names : [], // Ensure array
          codingSets: Array.isArray(data.coding_sets) ? data.coding_sets : [], // Ensure array
          behavioralSets: Array.isArray(data.behavioral_sets) ? data.behavioral_sets : [], // Ensure array
        });

        // Full coding set docs (question_slots, time_limit_minutes, allowed_languages)
        // for the read-only preview shown once a set is picked.
        try {
          const setsRes = await axios.get(`${host}/api/codingsets/`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const byName = {};
          (Array.isArray(setsRes.data) ? setsRes.data : []).forEach((s) => { byName[s.set_name] = s; });
          setCodingSetsData(byName);
        } catch {
          setCodingSetsData({});
        }
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
            await new Promise((resolve) => {
              sessionStorage.removeItem('accessToken');
              resolve();
            });
            toast.error(`Unauthorized: ${detail}`);
          } else if (status === 404) {
            toast.warning(`Not Found: ${detail}`);
          } else {
            toast.error(`Error: ${detail}`);
          }
        } else {
          toast.error('An unexpected error occurred. Please try again.');
        }
        setIsLoading(false);
      }
    };

    fetchCombinedData();
  }, [accessToken]);

  const CODING_DIFF_TIME = { Easy: 10, Medium: 20, Hard: 30, Custom: 20 };

  const fetchCodingSetProblems = async (setName) => {
    if (!setName) { setCodingSetProblems([]); setSelfHostedProblemIds(new Set()); return; }
    setIsFetchingSetProblems(true);
    try {
      const [setRes, libRes] = await Promise.all([
        axios.post(`${host}/api/coding-problems`, { set_name: setName }, { headers: { Authorization: `Bearer ${accessToken}` } }),
        axios.get(`${host}/api/available_problems`, { headers: { Authorization: `Bearer ${accessToken}` } }),
      ]);
      setCodingSetProblems(Array.isArray(setRes.data) ? setRes.data : []);
      const shIds = new Set((libRes.data.self_hosted || []).map(p => p.problem_id));
      setSelfHostedProblemIds(shIds);
    } catch {
      setCodingSetProblems([]);
      setSelfHostedProblemIds(new Set());
    } finally {
      setIsFetchingSetProblems(false);
    }
  };

  const handleViewProblem = async (prob) => {
    setViewingProblem({ problem_id: prob.problem_id, title: prob.title, difficulty: prob.difficulty });
    setViewingProblemLoading(true);
    try {
      const res = await axios.get(`${host}/api/problem/${prob.problem_id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setViewingProblem(res.data);
    } catch { /* keep stub visible */ } finally {
      setViewingProblemLoading(false);
    }
  };

  useEffect(() => {
    if (formData.selectedCodingSet && formData.selectedCodingSet !== 'NA') {
      fetchCodingSetProblems(formData.selectedCodingSet);
      if (!isFirstCodingSetLoad.current) {
        updateFormData({ codingQuestionConfig: [] });
      }
      isFirstCodingSetLoad.current = false;
    } else {
      setCodingSetProblems([]);
      isFirstCodingSetLoad.current = false;
    }
  }, [formData.selectedCodingSet]);

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const goToStep = (step) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  };

  const handleDrop = (draggedItem, targetBoxId, targetIndex) => {
    const { item, sourceBoxId } = draggedItem;

    const getItems = (boxId) => {
      if (boxId === 'required') return [...formData.requiredSkills];
      if (boxId === 'optional') return [...formData.optionalSkills];
      if (boxId === 'aiSuggestedSkills') return [...formData.aiSuggestedSkills];
      return [];
    };

    const updateItems = (boxId, newItems) => {
      if (boxId === 'required') {
        updateFormData({
          requiredSkills: newItems,
          errors: { ...formData.errors, requiredSkills: '' },
        });
      } else if (boxId === 'optional') {
        updateFormData({ optionalSkills: newItems });
      } else if (boxId === 'aiSuggestedSkills') {
        updateFormData({ aiSuggestedSkills: newItems });
      }
    };

    if (sourceBoxId === targetBoxId) {
      const items = getItems(sourceBoxId);
      const dragIndex = items.indexOf(item);

      if (dragIndex === -1 || dragIndex === targetIndex) return;

      const [reorderedItem] = items.splice(dragIndex, 1);
      items.splice(targetIndex, 0, reorderedItem);

      updateItems(sourceBoxId, items);
      return;
    }

    const sourceItems = getItems(sourceBoxId);
    const targetItems = getItems(targetBoxId);

    if (targetItems.includes(item)) return;

    if (targetBoxId === 'required' && targetItems.length >= 6) {
      if (!addSkillCooldownRef.current) {
        addSkillCooldownRef.current = true;
        toast.error('You can only add a maximum of 6 skills');
        setTimeout(() => {
          addSkillCooldownRef.current = false;
        }, 1000);
      }
      return;
    }

    const updatedSourceItems = sourceItems.filter((skill) => skill !== item);

    const updatedTargetItems = [...targetItems];
    updatedTargetItems.splice(targetIndex, 0, item);

    updateItems(sourceBoxId, updatedSourceItems);
    updateItems(targetBoxId, updatedTargetItems);
  };

  const handleGenerateJDWithAI = async () => {
    // Validate inputs first
    if (!formData.jobRole.trim()) {
      toast.error('Please enter a job role');
      return;
    }
    if (formData.minExp === '' || formData.maxExp === '') {
      toast.error('Please enter experience range');
      return;
    }

    if (
      !(formData.minExp === 0 && formData.maxExp === 0) &&
      parseInt(formData.minExp) >= parseInt(formData.maxExp)
    ) {
      toast.error('Maximum experience must be greater than minimum');
      return;
    }

    setIsGeneratingJD(true);
    try {
      const response = await fetch(`${host}/generate-jd-with-ai`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          jobRole: formData.jobRole,
          minExp: formData.minExp,
          maxExp: formData.maxExp,
        }),
      });

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.detail || 'Failed to generate JD');

      if (!result.is_valid) {
        updateFormData({
          errors: {
            ...formData.errors,
            jobRole:
              'Not a valid job role. Please enter a valid position title.',
          },
        });
        return;
      }

      updateFormData({
        responsibilities:
          result.job_description || result.responsibilities || '',
        errors: {
          ...formData.errors,
          jobRole: '',
          responsibilities: '',
        },
      });
      // toast.success("Job description generated successfully!");
    } catch (error) {
      console.error('Error generating JD:', error);
      toast.error('Failed to generate job description. Please try again.');
    } finally {
      setIsGeneratingJD(false);
    }
  };

  const handleJobRoleChange = (event) => {
    const value = event.target.value;
    updateFormData({
      jobRole: value,
      requiredSkills: [],
      optionalSkills: [],
      errors: {
        ...formData.errors,
        jobRole: value.trim() === '' ? ' ' : '',
      },
    });
  };

  const handleMinExpChange = (event) => {
    const value = parseInt(event.target.value) || 0; // Default to 0 if NaN
    updateFormData({ minExp: value });
    validateExperience(value, formData.maxExp);
    if (value <= formData.maxExp) {
      handleExperienceChange(value, formData.maxExp);
    }
  };

  const handleMaxExpChange = (event) => {
    const value = parseInt(event.target.value) || 1; // Default to 1 if NaN
    updateFormData({ maxExp: value });
    validateExperience(formData.minExp, value);
    if (value > formData.minExp) {
      handleExperienceChange(value, formData.minExp);
    }
  };

  const handleExperienceChange = (min, max) => {
    setExperience(parseInt((min + max) / 2));
  };

  const validateExperience = (min, max) => {
    const errors = {
      ...formData.errors,
      formError: '',
      minExp: '',
      maxExp: '',
    };

    // Check if any required field is empty
    if (min === '' || max === '') {
      errors.formError = 'Please fill in all required fields';
    } else if (min === 0 && max === 0) {
      // Fresher — valid, skip
    } else if (parseInt(min) >= parseInt(max)) {
      // Check minExp >= maxExp only if both are non-empty
      errors.maxExp = 'Maximum experience must be greater than minimum';
    }

    updateFormData({ errors });
  };

  const handleResponsibilitiesChange = (event) => {
    updateFormData({ responsibilities: event.target.value });
  };

  const handleLocationAdd = (e) => {
    if (e.key === 'Enter' && searchInput.trim() !== '') {
      updateFormData({
        searchLocations: [...formData.searchLocations, searchInput.trim()],
      });
      setSearchInput('');
    }
  };

  // Handle input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle removing a location
  const handleLocationRemove = (location) => {
    updateFormData({
      searchLocations: formData.searchLocations.filter(
        (loc) => loc !== location,
      ),
    });
  };

  const handleGenerateJD = async () => {
    if (selectedStyle === 'screening' || selectedStyle === 'custom') {
      const rawJD = {
        minExp: formData.minExp,
        maxExp: formData.maxExp,
        job_description: formData.responsibilities || '',
      };

      const generatedJD = {
        job_description: formData.responsibilities || '',
      };

      updateFormData({
        rawJD,
        generatedJD,
        isFineTuned: true,
      });
      return true;
    }
    if (formData.requiredSkills.length < 3) {
      updateFormData({
        errors: {
          ...formData.errors,
          requiredSkills: 'Please add at least 3 required skills',
        },
      });
      return;
    } else {
      updateFormData({
        errors: {
          ...formData.errors,
          requiredSkills: '',
        },
      });
    }
    setIsLoading2(true);
    const skills_list = {
      Hard: [],
      Medium: [],
      Easy: [],
    };

    const rawJD = {
      minExp: formData.minExp,
      maxExp: formData.maxExp,
      location: formData.location || '',
      Required_Skills: formData.requiredSkills,
      Optional_Skills: formData.optionalSkills,
      Roles_and_Responsibilities: formData.responsibilities
        ? formData.responsibilities.split('\n').filter((item) => item.trim())
        : [],
    };

    updateFormData({ rawJD });

    const experience =
      (parseInt(formData.minExp) + parseInt(formData.maxExp)) / 2;

    formData.requiredSkills.forEach((skill) => {
      if (experience < 4) {
        skills_list.Medium.push(skill);
      } else {
        skills_list.Hard.push(skill);
      }
    });

    formData.optionalSkills.forEach((skill) => {
      if (experience < 4) {
        skills_list.Easy.push(skill);
      } else {
        skills_list.Medium.push(skill);
      }
    });

    const jd = `jobrole: ${formData.jobRole}
                MinExperience: ${formData.minExp},
                MaxExperience: ${formData.maxExp}
                Location: ${formData.location}
                SkillsRequired:${JSON.stringify(skills_list)}
               `;
    const dataToSend = {
      description: jd,
      interview_type: formData.interview_type || 'technical',
      role_summary: formData.role_summary || '',
      question_design_context: formData.question_design_context || '',
    };

    try {
      const response = await fetch(`${host}/orgadmin/fineTuneJD`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(dataToSend),
      });
      const result = await response.json();
      console.log('Fine-tuned JD:', result.fineTunedJD);

      if (!response.ok) {
        toast.error(
          result.message ||
            'Something went wrong with the fine-tuning process.',
        );
        return false;
      }

      updateFormData({ generatedJD: result.fineTunedJD, isFineTuned: true });
      return true;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          await new Promise((resolve) => {
            sessionStorage.removeItem('accessToken');
            resolve();
          });
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404) {
          toast.warning(`Not Found: ${detail}`);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
      console.error('Error connecting to the server:', error);
      toast.error('Failed to connect to the server. Please try again.');
      return false;
    } finally {
      setIsLoading2(false);
    }
  };

  const handleAnalyze = async () => {
    const newErrors = {
      formError: '',
      jobRole: '',
      minExp: '',
      maxExp: '',
      requiredSkills: '',
      responsibilities: '',
      experience: '',
    };

    // Check if any required field is empty
    if (!jDPermissions.create_jd_v1_enabled) {
      setLimitModal({
        isOpen: true,
        featureKey: 'create_jd_v1_enabled',
        errorCode: 'FEATURE_LOCKED',
      });
      return;
    }
    if (
      jDPermissions.create_jd_v1_enabled &&
      jDPermissions.create_jd_v1_remaining <= 0
    ) {
      setLimitModal({
        isOpen: true,
        featureKey: 'create_jd_v1',
        errorCode: 'LIMIT_EXCEEDED',
      });
      return;
    }

    if (
      formData.jobRole.trim() === '' ||
      formData.minExp === '' ||
      formData.maxExp === ''
    ) {
      newErrors.formError = 'Please fill in all required fields';
    } else if (
      formData.minExp !== '' &&
      formData.maxExp !== '' &&
      !(formData.minExp === 0 && formData.maxExp === 0) &&
      parseInt(formData.minExp) >= parseInt(formData.maxExp)
    ) {
      // Check minExp >= maxExp only if both are non-empty
      newErrors.maxExp = 'Maximum experience must be greater than minimum';
    }

    if (
      formData.minExp === '' ||
      formData.maxExp === '' ||
      formData.minExp === undefined ||
      formData.maxExp === undefined
    ) {
      newErrors.experience = 'Experience is Required for Creating Job';
    }

    if (formData.responsibilities.trim() === '') {
      newErrors.responsibilities = 'Please add the job description';
    } else {
      const words = formData.responsibilities
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      const wordCount = words.length;

      if (wordCount < 50) {
        newErrors.responsibilities = `Job description must be at least 50 words (currently ${wordCount} words)`;
      // } else if (wordCount > 500) {
      //   newErrors.responsibilities = `Job description cannot exceed 500 words (currently ${wordCount} words)`;
      }
    }

    updateFormData({ errors: newErrors });
    if (Object.values(newErrors).some((error) => error !== '')) {
      return;
    }

    setCurrentSubStep(2);
  };

  const handleBackFromConfigureQuestions = () => {
    setCurrentSubStep(1);
  };

  const handleIncludeMCQ = () => {
    if (formData.isMCQIncluded) {
      updateFormData({
        isMCQIncluded: false,
        selectedSet: 'NA',
      });
      updateFormData({
        sequence: formData.sequence.filter((item) => item.id !== 'mcq'),
      });
    } else {
      updateFormData({
        isMCQIncluded: true,
        sets: formData.sets || [],
        sequence: [
          ...(formData.sequence || []),
          { id: 'mcq', label: 'Knowledge Test', mandatory: false },
        ],
      });
    }
  };

  const handleIncludeCoding = () => {
    if (formData.isCodingIncluded) {
      updateFormData({
        isCodingIncluded: false,
        selectedCodingSet: 'NA',
        CodingTimeLimit: 0,
        codingQuestionConfig: [],
        sequence: formData.sequence.filter((item) => item.id !== 'coding'),
      });
      setCodingSetProblems([]);
      setSelfHostedProblemIds(new Set());
    } else {
      updateFormData({
        isCodingIncluded: true,
        codingSets: formData.codingSets || [],
        sequence: [
          ...(formData.sequence || []),
          { id: 'coding', label: 'Coding Test', mandatory: false },
        ],
      });
    }
  };

  const handleIncludeTyping = () => {
    if (formData.isTypingIncluded) {
      updateFormData({
        isTypingIncluded: false,
        typingTimeLimit: 0,
      });
      updateFormData({
        sequence: formData.sequence.filter((item) => item.id !== 'typing'),
      });
    } else {
      updateFormData({
        isTypingIncluded: true,
        typingTimeLimit: 1,
        sequence: [
          ...(formData.sequence || []),
          { id: 'typing', label: 'Typing Test', mandatory: false },
        ],
      });
    }
  };

  const handleIncludeBehavioral = () => {
    if (formData.isBehavioralIncluded) {
      updateFormData({
        isBehavioralIncluded: false,
        selectedBehavioralSet: 'NA',
        sequence: formData.sequence.filter((item) => item.id !== 'behavioral'),
      });
    } else {
      updateFormData({
        isBehavioralIncluded: true,
        behavioralSets: formData.behavioralSets || [],
        sequence: [
          ...(formData.sequence || []),
          { id: 'behavioral', label: 'Behavioural Assessment', mandatory: false },
        ],
      });
    }
  };

  const handleQuestionChange = (index, newValue) => {
    const updatedEditedQuestions = [...formData.editedQuestions];
    updatedEditedQuestions[index] = newValue;
    const updatedOriginalQuestions = [...formData.questions];
    updatedOriginalQuestions[index] = newValue;
    updateFormData({
      editedQuestions: updatedEditedQuestions,
      questions: updatedOriginalQuestions,
    });
  };

  const handleAddSkills = () => {
    const experience =
      (parseInt(formData.minExp) + parseInt(formData.maxExp)) / 2;

    let updatedRequiredSkills = [...formData.requiredSkills];
    let updatedOptionalSkills = [...formData.optionalSkills];
    let updatedJD = formData.generatedJD
      ? { ...formData.generatedJD }
      : { Required_Skills: { Hard: [], Medium: [], Easy: [] } };

    if (formData.customRequiredSkill.trim()) {
      updatedRequiredSkills = [
        ...updatedRequiredSkills,
        formData.customRequiredSkill.trim(),
      ];
      if (experience > 4) {
        updatedJD.Required_Skills = {
          ...updatedJD.Required_Skills,
          Hard: [
            ...(updatedJD.Required_Skills?.Hard || []),
            formData.customRequiredSkill.trim(),
          ],
        };
      } else {
        updatedJD.Required_Skills = {
          ...updatedJD.Required_Skills,
          Medium: [
            ...(updatedJD.Required_Skills?.Medium || []),
            formData.customRequiredSkill.trim(),
          ],
        };
      }
    }

    if (formData.customOptionalSkill.trim()) {
      updatedOptionalSkills = [
        ...updatedOptionalSkills,
        formData.customOptionalSkill.trim(),
      ];

      if (experience > 4) {
        updatedJD.Required_Skills = {
          ...updatedJD.Required_Skills,
          Medium: [
            ...(updatedJD.Required_Skills?.Medium || []),
            formData.customOptionalSkill.trim(),
          ],
        };
      } else {
        updatedJD.Required_Skills = {
          ...updatedJD.Required_Skills,
          Easy: [
            ...(updatedJD.Required_Skills?.Easy || []),
            formData.customOptionalSkill.trim(),
          ],
        };
      }
    }

    updateFormData({
      requiredSkills: updatedRequiredSkills,
      optionalSkills: updatedOptionalSkills,
      generatedJD: updatedJD,
      customRequiredSkill: '',
      customOptionalSkill: '',
    });
  };
  const handleSaveCustomQuestions = async () => {
    const trimmedQuestions = formData.customQuestions.trim();
    console.log({ trimmedQuestions });
    if (!trimmedQuestions) {
      setErrorMessage('Please enter at least one custom question');
      return;
    }

    // Check max 10 limit
    if (formData.questions.length >= 10) {
      setErrorMessage('Maximum 10 custom questions allowed');
      return;
    }

    setErrorMessage('');
    setIsSavingQuestions(true);
    const requestUrl = `${host}/upload-custom-questions/`;

    try {
      const response = await axios.post(
        requestUrl,
        { questions: trimmedQuestions },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const { isValid, questions, message } = response.data;
      if (!isValid) {
        setErrorMessage(
          message || 'The input does not contain valid questions.',
        );
        return;
      }
      updateFormData({
        questions: questions,
        editedQuestions: questions,
      });
      setErrorMessage('');
    } catch (error) {
      console.error('Error saving custom questions:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      if (error.response?.status === 400) {
        setErrorMessage(
          error.response.data.message ||
            'The input does not contain valid questions. Please enter valid questions.',
        );
      } else if (error.response?.status === 401) {
        setErrorMessage('Authentication failed. Please log in again.');
      } else {
        setErrorMessage(
          'Failed to save custom questions. Please try again or contact support.',
        );
      }
    } finally {
      setIsSavingQuestions(false); // Reset button-specific loading state
    }
  };

  const handleFinalize = async () => {
    // Collected rather than returned on the first failure, so every invalid field
    // on the step highlights at once instead of one per attempt.
    const stepErrors = {
      interviewTimeLimit: '',
      selectedSet: '',
      selectedCodingSet: '',
      typingTimeLimit: '',
      selectedBehavioralSet: '',
    };
    if (formData.interview_timelimit == null) {
      stepErrors.interviewTimeLimit =
        selectedStyle === 'screening'
          ? 'Please select a screening duration (10, 15, or 20 minutes)'
          : 'Please set the interview duration';
    } else if (formData.interview_timelimit < 5) {
      stepErrors.interviewTimeLimit = 'Interview should be at least 5 mins!';
    }
    if (
      formData.isMCQIncluded &&
      (!formData.selectedSet || formData.selectedSet === 'NA')
    ) {
      stepErrors.selectedSet = 'Please select a set for the MCQ tests.';
    }
    if (
      formData.isCodingIncluded &&
      (!formData.selectedCodingSet || formData.selectedCodingSet === 'NA')
    ) {
      stepErrors.selectedCodingSet = 'Please select a coding set.';
    }
    if (formData.isTypingIncluded && formData.typingTimeLimit < 1) {
      stepErrors.typingTimeLimit = 'Typing test should be at least 1 minutes long.';
    }
    if (
      formData.isBehavioralIncluded &&
      (!formData.selectedBehavioralSet || formData.selectedBehavioralSet === 'NA')
    ) {
      stepErrors.selectedBehavioralSet =
        'Please select a set for the Behavioural assessment.';
    }
    updateFormData({ errors: { ...formData.errors, ...stepErrors } });
    const firstFieldError = Object.values(stepErrors).find(Boolean);
    if (firstFieldError) {
      toast.warn(firstFieldError);
      return false;
    }
    if (selectedStyle !== 'custom' && formData.questions.length > 10) {
      toast.warn('Maximum 10 custom questions allowed...');
      return false;
    }
    if (selectedStyle === 'custom' && formData.questions.length === 0) {
      toast.warn(
        "Please add at least one question for 'Bring Your Own Questions' mode.",
      );
      return false;
    }
    if (formData.questions[formData.questions.length - 1]?.trim() === '') {
      toast.warn('Please remove empty question at the end!');
      return false;
    }
    if (
      formData.isCustomQuestionsIncluded &&
      formData.customQuestions.trim() === '' &&
      formData.questions.length === 0
    ) {
      toast.warn('Custom Questions cannot be Empty!');
      return false;
    }
    updateFormData({ questions: [...formData.editedQuestions] });
    if (!formData.selectedServices.sourcing) {
      updateFormData({
        candidatesRequired: 0,
        searchLocations: [],
      });
    } else {
      if (formData.candidatesRequired <= 0) {
        toast.warn('Please specify candidates required from sourcing!');
        return false;
      }
      if (formData.searchLocations.length === 0) {
        toast.warn(
          'Please specify at least one location to search candidates!',
        );
        return false;
      }
    }
    if (formData.selectedServices.screening) {
      if (formData.companyName.trim() === '') {
        toast.warn('Please enter Company Name');
        return false;
      }
      if (formData.companyDescription.trim() === '') {
        toast.warn('Please enter a short description about Company');
        return false;
      }
      if (
        formData.workLocations.length === 0 &&
        formData.workLocationType !== 'remote'
      ) {
        toast.warn('Please enter at least 1 Work Location');
        return false;
      }
      if (!formData.maxCTC || formData.maxCTC <= 0) {
        toast.warn('Please Enter the Max CTC');
        return false;
      }
    } else {
      updateFormData({
        companyName: '',
        companyDescription: '',
        workLocations: [],
        maxCTC: '',
      });
    }
    updateFormData({ showInterviewConfig: true });
    return true;
  };
  const handleUpdateJD = async () => {
    console.log('handleUpdateJD - formData:', {
      interview_type: formData.interview_type,
      role_summary: formData.role_summary,
      question_design_context: formData.question_design_context,
    });

    const dataToSend = {
      description: {
        jd_id: formData.jd_id || null,
        org_id: org_id,
        jobrole: formData.jobRole,
        minExp: parseInt(formData.minExp),
        maxExp: parseInt(formData.maxExp),
        location: formData.location || '',
        job_description: formData.generatedJD.job_description || '',
        Required_Skills: {
          Easy: formData.generatedJD.Required_Skills?.Easy || [],
          Medium: formData.generatedJD.Required_Skills?.Medium || [],
          Hard: formData.generatedJD.Required_Skills?.Hard || [],
        },
        GoodtoHave: formData.generatedJD.GoodtoHave || [],
        Tools_and_Technologies:
          formData.generatedJD['Tools & Technologies'] || [],
        Roles_and_Responsibilities:
          formData.generatedJD['Roles & Responsibilities'] || [],
        Raw_Job_Description: formData.responsibilities,
        selectedInterviewType: 'Technical Interview',
        rawJD: formData.rawJD,
        interview_type: formData.interviewType || 'technical',
        role_summary: formData.roleSummary || '',
        question_design_context: formData.questionDesignContext || '',
        interview_timelimit: parseInt(formData.interview_timelimit),
        // Time limit and pass mark are no longer sent from here — the backend
        // derives both straight from the chosen MCQ set, their only author.
        selectedSet: formData.isMCQIncluded ? formData.selectedSet : 'NA',
        // Time limit, judge criteria, and question config are no longer sent from
        // here — the backend derives them straight from the chosen coding set.
        selectedCodingSet: formData.isCodingIncluded
          ? formData.selectedCodingSet
          : 'NA',
        typingTimeLimit: formData.isTypingIncluded
          ? parseInt(formData.typingTimeLimit)
          : 0,
        // Empty string, not 'NA' like MCQ's selectedSet — the backend does
        // `selectedBehavioralSet or None`, and unlike mcq_set_id (whose downstream
        // gating reads mcq_time_limit, not the set id), every behavioral_status gate
        // in this codebase checks selected_behavioral_set's own truthiness directly,
        // so a literal "NA" string here would read as "a set is selected".
        selectedBehavioralSet: formData.isBehavioralIncluded
          ? formData.selectedBehavioralSet
          : '',
        enable_candidate_form: formData.isCandidateFormIncluded || false,
        customQuestions: formData.questions || [],
        selectedServices: {
          sourcing: formData.selectedServices.sourcing,
          screening: formData.selectedServices.screening,
          assessment: formData.selectedServices.assessment,
        },
        assessmentSequence: formData.assessmentSequence,
        jobType: formData.jobType,
        companyName: formData.companyName,
        companyDescription: formData.companyDescription,
        candidatesRequired: parseInt(formData.candidatesRequired),
        searchLocations: formData.searchLocations,
        workLocationType: formData.workLocationType,
        workLocations:
          formData.workLocationType === 'remote'
            ? 'Remote'
            : formData.workLocations,
        noticePeriod: parseInt(formData.noticePeriod),
        noticeStrictness: formData.noticeStrictness,
        maxCTC: parseInt(formData.maxCTC),
        ctcStrictness: formData.ctcStrictness,
        Pre_Written_Questions: formData.Pre_Written_Questions,
        Topics_To_Ask: formData.Topics_To_Ask,
        questionType: getInterviewTypeLabel(selectedStyle),
        preGeneratedTopics: formData.preGeneratedTopics, // NEW: Pass pre-generated topics to skip regeneration
      },
    };

    try {
      setIsLoading(true);
      console.log('Create JD Data', dataToSend);
      const response = await fetch(`${host}/store_final_jd`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(dataToSend),
      });
      const result = await response.json();
      if (!response.ok) {
        const errorMessage = result.message || (await response.text());
        console.error(
          'Error updating JD:',
          errorMessage,
          'Status Code:',
          response.status,
        );

        alert(
          errorMessage ||
            'Something went wrong while updating the job description.',
        );
      } else {
        dispatch(toggleisjdcreated(true));
        dispatch(guideJobCreated());
        console.log('Success response:', result);
        // toast.success("Job description updated successfully.");
        localStorage.removeItem('createJDFormData');
        localStorage.removeItem('createJDSections');
        setFormData({
          jobRole: '',
          minExp: 0,
          maxExp: 1,
          location: '',
          responsibilities: '',
          requiredSkills: [],
          optionalSkills: [],
          aiSuggestedSkills: [],
          generatedJD: {},
          isAnalyzed: false,
          isFineTuned: false,
          customRequiredSkill: '',
          customOptionalSkill: '',
          showInterviewConfig: false,
          interview_timelimit: null,
          isMCQIncluded: false,
          isCodingIncluded: false,
          isTypingIncluded: false,
          isBehavioralIncluded: false,
          CodingTimeLimit: 10,
          CodingJudgingCriteria: '1',
          codingQuestionConfig: [],
          typingTimeLimit: 1,
          selectedSet: '',
          selectedCodingSet: '',
          selectedBehavioralSet: '',
          sets: [],
          codingSets: [],
          behavioralSets: [],
          isCustomQuestionsIncluded: false,
          customQuestions: '',
          selectedServices: {
            sourcing: false,
            screening: false,
            assessment: true,
          },
          editedQuestions: [],
          questions: [],
          candidatesRequired: 5,
          searchLocations: [],
          assessmentSequence: ['interview'],
          companyName: '',
          companyDescription: '',
          jobType: 'fulltime',
          workLocationType: 'office',
          workLocationInput: '',
          workLocations: [],
          noticePeriod: 0,
          noticeStrictness: 'strict',
          maxCTC: '',
          ctcStrictness: 'strict',
          errors: {
            jobRole: '',
            minExp: '',
            maxExp: '',
            requiredSkills: '',
            interviewTimeLimit: '',
            selectedSet: '',
            selectedCodingSet: '',
            typingTimeLimit: '',
            selectedBehavioralSet: '',
          },
          sequence: [
            {
              id: 'interview',
              label: 'Technical Interview',
              mandatory: true,
              icon: '👥',
            },
          ],
          interview_type: '',
          role_summary: '',
          question_design_context: '',
          Pre_Written_Questions: {},
          Topics_To_Ask: {},
          preGeneratedTopics: {},
        });

        // Reset sections to initial state
        setSections({
          sourcing: {
            isExpanded: false,
            isChecked: false,
            content:
              'Configure your candidate sourcing preferences, channels, and automation settings.',
          },
          screening: {
            isExpanded: false,
            isChecked: false,
            content:
              'Set up your screening criteria, questionnaires, and filtering parameters.',
          },
          assessment: {
            isExpanded: true,
            isChecked: true,
            content:
              'Customize your assessment methods, scoring criteria, and evaluation process.',
          },
        });

        // Reset other state variables
        setExperience(0);
        setIsAddSkillOpen(false);
        setCurrentStep(1); // Navigate back to step 1
        setSearchInput('');
        disableGuard();
        navigate('/org-admin/created-jds', { state: { showSuccessToast: true } });
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data.detail;
        if (status === 401) {
          await new Promise((resolve) => {
            sessionStorage.removeItem('accessToken');
            resolve();
          });
          toast.error(`Unauthorized: ${detail}`);
        } else if (status === 404) {
          toast.warning(`Not Found: ${detail}`);
        } else {
          toast.error(`Error: ${detail}`);
        }
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
      console.error('Error connecting to the server:', error);
      toast.error('Failed to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }
  if (isSkillsLoading) {
    return <AIProcessingLoader />;
  }
  if (isLoading2) {
    return <JobDescriptionLoader />;
  }
  if (isGeneratingQuestions) {
    return <QuestionGenerationLoader />;
  }

  const isStepOneNextDisabled =
    currentStep === 1 &&
    currentSubStep === 1 &&
    (!formData.jobRole.trim() ||
      formData.minExp === '' ||
      formData.maxExp === '' ||
      !formData.responsibilities.trim());

  const handleChange = (e, skill) => {
    const value = e.target.value;

    // Update main form data and mark question as edited
    setFormData((prev) => {
      const updatedPreWrittenQuestions = {};
      const updatedTopicsToAsk = { ...prev.Topics_To_Ask };

      for (const skill in prev.Pre_Written_Questions) {
        updatedPreWrittenQuestions[skill] = prev.Pre_Written_Questions[
          skill
        ].map((q, i) => {
          if (`${skill}-${i}` === editingIndex) {
            // Question edited - remove this topic from Topics_To_Ask
            if (updatedTopicsToAsk[skill]) {
              updatedTopicsToAsk[skill] = updatedTopicsToAsk[skill].filter(
                (t) => t !== q.topic,
              );
              if (updatedTopicsToAsk[skill].length === 0) {
                delete updatedTopicsToAsk[skill];
              }
            }
            return { ...q, question: value, edited: true };
          }
          return q;
        });
      }

      return {
        ...prev,
        Pre_Written_Questions: updatedPreWrittenQuestions,
        Topics_To_Ask: updatedTopicsToAsk,
      };
    });
  };

  const handleCommitEdit = (value, skill) => {
    console.log(`Question committed for ${skill}: ${value}`);
  };





  const renderMagicBox = () => {
    const QUESTION_STYLE_CARDS = [
      {
        id: 'screening',
        label: 'Initial Screening',
        description:
          'A quick check to see if the candidate meets the basic role requirements',
        details:
          'This assessment type focuses on verifying fundamental qualifications and basic competencies. It helps quickly filter candidates who meet minimum requirements before proceeding to more detailed evaluations. Questions are designed to be straightforward and cover essential qualifications.',
      },
      {
        id: 'knowledge',
        label: 'Core Knowledge Evaluation',
        description:
          'Focuses on the candidates understanding of key concepts and fundamentals',
        details:
          "This assessment evaluates the candidate's theoretical understanding and foundational knowledge in their field. It tests comprehension of core principles, methodologies, and best practices. Questions are designed to assess depth of understanding rather than memorization.",
      },
      {
        id: 'custom',
        label: 'Bring Your Own Questions',
        description: 'Use your own curated questions in the AI Interview',
        details:
          'This mode runs the interview using only the questions you provide. The interview continues until all questions are answered. No AI-generated questions or follow-ups are added.',
      },
    ];

    const handleCardClick = async (styleId) => {
      setSelectedStyle(selectedStyle === styleId ? null : styleId);
    };

    const handleGenerate = async () => {
      if (!selectedStyle) return;

      setSkillsLoading(true);
      try {
        const response = await axios.post(
          `${host}/analyze`,
          {
            jobRole: formData.jobRole,
            minExp: formData.minExp,
            maxExp: formData.maxExp,
            responsibilities:
              formData.responsibilities.trim() === ''
                ? 'Not Provided'
                : formData.responsibilities,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
        const newAiSkills = response.data.skills || [];
        const existingRequiredSkills = formData.requiredSkills || [];

        const updates = {
          aiSuggestedSkills: response.data.skills || [],
          originalAISkills: response.data.skills || [],
          requiredSkills: [],
          aiSuggestedSkills: newAiSkills.filter(
            (skill) => !existingRequiredSkills.includes(skill),
          ),
          originalAISkills: newAiSkills,
          requiredSkills: existingRequiredSkills,
          isAnalyzed: true,
          interview_type: response.data.interview_type || 'technical', // Fallback
          role_summary: response.data.role_summary || '',
          question_design_context: response.data.question_design_context || '',
          questionTypeLabel: getInterviewTypeLabel(selectedStyle),
        };
        updateFormData(updates);
        setCurrentStep(2);
        setCurrentSubStep(1);
        return true;
      } catch (error) {
        if (error.response) {
          const status = error.response.status;
          const detail = error.response.data.detail;
          if (status === 401) {
            await new Promise((resolve) => {
              sessionStorage.removeItem('accessToken');
              resolve();
            });
            toast.error(`Unauthorized: ${detail}`);
          } else if (status === 404) {
            toast.warning(`Not Found: ${detail}`);
          } else {
            toast.error(`Error: ${detail}`);
          }
        } else {
          toast.error('An unexpected error occurred. Please try again.');
        }
        console.error('Error sending data to backend:', error);
        toast.error('Failed to connect to the server. Please try again.');
        return false;
      } finally {
        setSkillsLoading(false);
      }
    };

    const selectedCard = QUESTION_STYLE_CARDS.find(
      (card) => card.id === selectedStyle,
    );

    return (
      <div className="w-full px-12 space-y-6">
        <h2 className="text-lg font-light dark:text-white text-textPrimary">
          Next, choose how you'd like to conduct the interview
        </h2>
        {/* 3-Column Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {QUESTION_STYLE_CARDS.map((card) => {
            const isSelected = selectedStyle === card.id;
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => handleCardClick(card.id)}
                className={`
              w-58 p-4 rounded-xl border transition-all duration-200 relative
               ${
                 isSelected
                   ? 'text-white dark:shadow-lg shadow-sm border-transparent'
                   : 'border-gray-700/50 dark:hover:bg-white/10 dark:hover:border-white/30 hover:border-gray-300 text-[#CCCCCC]'
               }
            `}
                style={
                  isSelected
                    ? {
                        /* Dark Mode Gradient Border */
                        ...(theme === 'dark' && {
                          background:
                            'linear-gradient(#1a1a2e, #1a1a2e) padding-box, linear-gradient(to right, #3b82f6, #ec4899) border-box',
                          border: '1px solid transparent',
                        }),

                        /* LIGHT MODE Gradient Border */
                        ...(theme === 'light' && {
                          background:
                            'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(to right, #ec4899, #3b82f6) border-box',
                          border: '1px solid transparent',
                        }),
                      }
                    : {}
                }
              >
                <div className="flex justify-center mb-2">
                  <h3 className="text-md dark:font-light font-semibold dark:text-white text-textPrimary h-12 flex items-center pr-2">
                    {card.label}
                  </h3>
                  {isSelected && (
                    <div className="absolute top-2.5 right-2.5 h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-pink-500 flex justify-center items-center flex-shrink-0">
                      <svg
                        className="h-4 w-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                <p className="text-xs dark:text-gray-400 text-textSecondary h-16 overflow-hidden">
                  {card.description}
                </p>
                {isSelected && (
                  <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2">
                    <svg
                      width="24"
                      height="12"
                      viewBox="0 0 24 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <linearGradient
                          id="arrowGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop offset="0%" stopColor="#3b82f6" />
                          <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M12 12L0 0H24L12 12Z"
                        fill="url(#arrowGradient)"
                      />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {selectedCard && (
          <div className="dark:glass-effect bg-bgPrimary w-full px-6 py-4 rounded-xl border border-gray-700/50 dark:shadow-lg shadow-sm">
            <h3 className="text-lg font-medium dark:text-purple-400 text-textPrimary mb-2">
              Details
            </h3>
            <p className="text-sm dark:text-gray-300 text-textSecondary leading-relaxed">
              {selectedCard.details}
            </p>
          </div>
        )}

        {/* Navigation Buttons */}
        <NavigationButtons
          onBack={handleBackFromConfigureQuestions}
          onNext={async () => {
            if (selectedStyle === 'screening' || selectedStyle === 'custom') {
              const success = await handleGenerateJD();
              if (success) {
                setCurrentStep(3);
                setCurrentSubStep(1);
              }
            } else {
              handleGenerate();
            }
          }}
          nextDisabled={!selectedStyle}
          showborderbtn={true}
        />
      </div>
    );
  };

  const renderStep3 = () => {
    // Calculate total skills and default time for knowledge assessment
    const totalSkillsCount = getTotalSkillsCount(formData.generatedJD);
    const defaultKnowledgeTime = getDefaultTimeForSkills(totalSkillsCount);

    return (
      <div className="w-full px-12 space-y-6">
        <div className="flex items-start justify-center gap-8 w-full">
          {/* LEFT SECTION → DURATION SELECTION */}
          <div className="flex-[1]">
            <div className="flex items-center gap-2 -mt-8">
              <h2 className="text-lg font-light dark:text-white text-textPrimary my-4">
                {selectedStyle === 'screening'
                  ? 'Select screening interview duration'
                  : "Then, let's set the interview duration"}
              </h2>
              <div className="group relative">
                <Info
                  size={18}
                  className="text-blue-500 dark:text-blue-400 cursor-help hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
                />
                <div className="absolute left-0 top-6 w-64 p-3 bg-blue-500/90 dark:bg-blue-600/90 backdrop-blur-sm text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg border border-blue-400/30">
                  <p className="leading-relaxed">
                    The indicated duration is an{' '}
                    <span className="font-semibold"> estimate </span> and may
                    vary based on responses.
                  </p>
                </div>
              </div>
            </div>
            {selectedStyle !== 'screening' && (
              <div className="my-2 text-sm dark:text-white text-textPrimary">
                <span>
                  Timings are approximate. The AI may extend them if needed.
                </span>
              </div>
            )}

            {/* Screening JD: Fixed time buttons (10/15/20 min) */}
            <div className={formData.errors?.interviewTimeLimit ? 'rounded-lg border border-red-500 p-3' : ''}>
            {selectedStyle === 'screening' ? (
              <div className="flex flex-col space-y-4 w-full">
                <div className="flex gap-4">
                  {SCREENING_TIME_OPTIONS.map((option) => (
                    <button
                      key={option.mins}
                      type="button"
                      onClick={() =>
                        updateFormData({
                          interview_timelimit: option.mins,
                          isInterviewTimeManuallySet: true,
                          errors: { ...formData.errors, interviewTimeLimit: '' },
                        })
                      }
                      className={`relative flex items-center w-[15%] space-x-2 justify-between px-6 py-4 rounded-lg border transition-all duration-200 ${
                        formData.interview_timelimit === option.mins
                          ? 'text-white'
                          : 'border-gray-700/50 hover:border-gray-500 text-[#CCCCCC]'
                      }`}
                      style={
                        formData.interview_timelimit === option.mins
                          ? {
                              background:
                                theme === 'dark'
                                  ? 'linear-gradient(#140F1C, #140F1C) padding-box, linear-gradient(to right, #3b82f6, #ec4899) border-box'
                                  : 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(to right, #ec4899, #3b82f6) border-box',
                              border: '1px solid transparent',
                            }
                          : {}
                      }
                    >
                      <span className="text-sm font-medium dark:text-white text-textPrimary w-fit pr-4">
                        ~{option.mins} Min
                      </span>

                      <div
                        className={`h-5 w-5 rounded-full flex justify-center items-center ${
                          formData.interview_timelimit === option.mins
                            ? 'bg-gradient-to-r from-blue-500 to-pink-500'
                            : 'hidden'
                        }`}
                      >
                        {formData.interview_timelimit === option.mins && (
                          <Check size={12} className="text-white" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : selectedStyle === 'custom' ? (
              <div className="flex flex-col gap-3">
                <div className="flex gap-4">
                  {CUSTOM_TIME_OPTIONS.map((mins) => (
                    <button
                      key={mins}
                      type="button"
                      onClick={() =>
                        updateFormData({
                          interview_timelimit: mins,
                          isInterviewTimeManuallySet: true,
                          errors: { ...formData.errors, interviewTimeLimit: '' },
                        })
                      }
                      className={`relative flex items-center justify-between px-6 py-4 rounded-lg border transition-all duration-200 ${
                        formData.interview_timelimit === mins
                          ? 'text-white'
                          : 'border-gray-700/50 hover:border-gray-500 text-[#CCCCCC]'
                      }`}
                      style={
                        formData.interview_timelimit === mins
                          ? {
                              background:
                                theme === 'dark'
                                  ? 'linear-gradient(#140F1C, #140F1C) padding-box, linear-gradient(to right, #3b82f6, #ec4899) border-box'
                                  : 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(to right, #ec4899, #3b82f6) border-box',
                              border: '1px solid transparent',
                            }
                          : {}
                      }
                    >
                      <span className="text-sm font-medium dark:text-white text-textPrimary w-fit mr-4">
                        ~{mins} Min
                      </span>
                      <div
                        className={`h-5 w-5 rounded-full flex justify-center items-center ${formData.interview_timelimit === mins ? 'bg-gradient-to-r from-blue-500 to-pink-500' : 'hidden'}`}
                      >
                        {formData.interview_timelimit === mins && (
                          <Check size={12} className="text-white" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                {formData.interview_timelimit && (
                  <p className="text-sm dark:text-gray-400 text-textSecondary">
                    Recommended questions for {formData.interview_timelimit}{' '}
                    minutes:{' '}
                    <span className="dark:text-white text-textPrimary font-medium">
                      {Math.floor(formData.interview_timelimit / 2.5)}
                    </span>
                  </p>
                )}
                {formData.interview_timelimit && formData.questions.length > Math.floor(formData.interview_timelimit / 2.5) && (
                  <p className="w-fit text-sm dark:text-[#f7dd88] text-textSecondary bg-yellow-50/50 dark:bg-yellow-200/25 border border-yellow-200 dark:border-yellow-200/30 rounded-md px-2 py-1">
                    If the number of questions exceeds the recommended count, the interview duration will be longer than selected.
                  </p>
                )}
              </div>
            ) : (
              /* Knowledge Assessment: Fixed time buttons (30/45/60/90 min) */
              <div className="flex flex-col space-y-4">
                <div className="flex gap-4">
                  {KNOWLEDGE_TIME_OPTIONS.map((mins) => {
                    // Use default time based on skills if not manually set
                    const currentTime = formData.isInterviewTimeManuallySet
                      ? formData.interview_timelimit
                      : formData.interview_timelimit || defaultKnowledgeTime;

                    return (
                      <div className="">
                        <button
                          key={mins}
                          type="button"
                          onClick={() =>
                            updateFormData({
                              interview_timelimit: mins,
                              isInterviewTimeManuallySet: true,
                              errors: { ...formData.errors, interviewTimeLimit: '' },
                            })
                          }
                          className={`flex-1 relative flex items-center justify-between px-6 py-4 rounded-lg border transition-all duration-200 ${
                            currentTime === mins
                              ? 'text-white'
                              : 'border-gray-700/50 hover:border-gray-500 text-[#CCCCCC]'
                          }`}
                          style={
                            currentTime === mins
                              ? {
                                  background:
                                    theme === 'dark'
                                      ? 'linear-gradient(#140F1C, #140F1C) padding-box, linear-gradient(to right, #3b82f6, #ec4899) border-box'
                                      : 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(to right, #ec4899, #3b82f6) border-box',
                                  border: '1px solid transparent',
                                }
                              : {}
                          }
                        >
                        <span className="text-sm font-medium pr-4 dark:text-white text-textPrimary">
                            ~{mins} Min
                          </span>

                          <div
                            className={`h-5 w-5 rounded-full flex justify-center items-center ${
                              currentTime === mins
                                ? 'bg-gradient-to-r from-blue-500 to-pink-500'
                                : ''
                            }`}
                          >
                            {currentTime === mins && (
                              <Check size={12} className="text-white" />
                            )}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
                {/* Warning Banner for Knowledge Assessment */}
                {formData.isInterviewTimeManuallySet &&
                  formData.interview_timelimit !== defaultKnowledgeTime && (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 w-[70%]">
                      <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-500/90 leading-relaxed">
                        The updated interview duration may not allow full skill
                        coverage. For best results, we suggest the system
                        recommended time.
                      </p>
                    </div>
                  )}
              </div>
            )}
            {formData.errors?.interviewTimeLimit && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-2">
                {formData.errors.interviewTimeLimit}
              </p>
            )}
            </div>
          </div>
        </div>

        <div className="w-full border-t border-gray-700/50"></div>

        <div className="-mt-4">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2 mb-2">
              <h2 className="text-lg font-light dark:text-white text-textPrimary mb-2">
                {selectedStyle !== 'custom'
                  ? 'Want to include your own questions? Add them here '
                  : 'Add your questions here '}
                {selectedStyle !== 'custom' ? (
                  <span className="dark:text-gray-400 text-textSecondary italic text-md">
                    (optional)
                  </span>
                ) : (
                  <span className="text-red-400 text-sm">*</span>
                )}
              </h2>
              {formData.questions.length > 0 && (
                <span className="text-lg font-light dark:text-white text-textPrimary mb-2">
                  ({formData.questions.length} question{formData.questions.length !== 1 ? 's' : ''}{selectedStyle !== 'custom' ? ' / 10' : ''})
                </span>
              )}
            </div>
            {formData.questions.length === 0 ? (
              <div className="flex flex-col space-y-2">
                <div
                  className={`w-[55%] pr-2 flex flex-row items-center justify-between ${theme === 'dark' && 'glass-effect focus:ring-[#5A4479]'} ${theme !== 'dark' && 'border border-gray-300 focus:border-gray-400'} ${errorMessage ? 'border-red-500' : ''} rounded-full transition`}
                >
                  <input
                    type="text"
                    placeholder="Type a question and press Enter or click Add"
                    value={formData.customQuestions}
                    onClick={() => {
                      if (selectedStyle === 'custom' && !formData.interview_timelimit) {
                        toast.warn('Please select the interview duration first');
                      }
                    }}
                    onChange={(e) => {
                      if (selectedStyle === 'custom' && !formData.interview_timelimit) {
                        return;
                      }
                      setFormData({
                        ...formData,
                        customQuestions: e.target.value,
                      });
                      setErrorMessage('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (selectedStyle === 'custom' && !formData.interview_timelimit) {
                          toast.warn('Please select the interview duration first');
                          return;
                        }
                        if (formData.customQuestions.trim()) {
                          handleSaveCustomQuestions();
                        }
                      }
                    }}
                    className={`${
                      theme === 'dark'
                        ? 'bg-transparent focus:ring-0 focus:outline-none'
                        : 'bg-transparent focus:border-gray-400 focus:ring-0 focus:outline-none'
                    } 
                      placeholder:text-sm dark:placeholder:text-gray-500 placeholder:italic dark:text-white text-textSecondary w-11/12 pl-4 pr-14 py-3 rounded-full transition`}
                  />
                  <button
                    onClick={handleSaveCustomQuestions}
                    disabled={
                      isSavingQuestions ||
                      !formData.customQuestions.trim() ||
                      (selectedStyle !== 'custom' &&
                        formData.questions.length >= 10)
                    }
                    className={`${theme === 'dark' ? 'button' : 'border-btn !text-textSecondary'} text-[13px] min-w-[25px] !px-4 !py-2 text-white transition-transform flex items-center justify-center ${
                      isSavingQuestions ||
                      !formData.customQuestions.trim() ||
                      formData.questions.length >= 10
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    {isSavingQuestions ? (
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <span>Add</span>
                    )}
                  </button>
                </div>
                {errorMessage && (
                  <p className="text-red-400 text-sm mt-1 ml-4">
                    {errorMessage}
                  </p>
                )}
              </div>
            ) : (
              <div className="relative">
                <ul
                  style={{
                    listStyleType: 'none',
                    padding: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}
                >
                  {formData.questions.map((question, index) => (
                    <li key={index} className="mb-2.5 flex items-center w-3/4">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-pink-500 to-blue-500 flex items-center justify-center text-white text-xs font-medium mr-3 flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="relative flex-grow">
                        <input
                          value={
                            typeof question === 'string'
                              ? question
                              : question.question
                          }
                          onChange={(e) =>
                            handleQuestionChange(index, e.target.value)
                          }
                          className="dark:glass-effect dark:bg-none bg-bgPrimary w-full py-2.5 pl-3 pr-10 dark:text-white text-textSecondary font-light rounded-full border border-gray-500 text-sm"
                        />
                        <button
                          onClick={() =>
                            setFormData({
                              ...formData,
                              questions: formData.questions.filter(
                                (_, i) => i !== index,
                              ),
                              editedQuestions: formData.editedQuestions.filter(
                                (_, i) => i !== index,
                              ),
                            })
                          }
                          className="absolute right-6 top-1/2 -translate-y-1/2 text-red-500 cursor-pointer text-2xl font-medium"
                        >
                          −
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                {formData.questions.length > 0 && (
                  <div className="flex justify-between items-center w-3/4 mt-3">
                    <div
                      className={`text-sm ${formData.questions.length >= 10 ? 'text-red-500 font-medium' : 'dark:text-gray-400 text-textSecondary'}`}
                    >
                      {/* {formData.questions.length}
                      {selectedStyle !== 'custom' ? ' / 10' : ''} question
                      {formData.questions.length !== 1 ? 's' : ''}
                      {selectedStyle !== 'custom' &&
                        formData.questions.length >= 10 &&
                        ' (Maximum reached)'} */}
                    </div>
                    <button
                      onClick={() => {
                        if (
                          selectedStyle !== 'custom' &&
                          formData.questions.length >= 10
                        ) {
                          alert('Maximum 10 custom questions allowed');
                          return;
                        }

                        const lastQuestion =
                          formData.editedQuestions[
                            formData.editedQuestions.length - 1
                          ] || '';

                        if (!lastQuestion.trim()) {
                          alert(
                            'Please fill the previous question before adding a new one.',
                          );
                          return;
                        }

                        setFormData({
                          ...formData,
                          questions: [...formData.questions, ''],
                          editedQuestions: [...formData.editedQuestions, ''],
                        });
                      }}
                      disabled={
                        selectedStyle !== 'custom' &&
                        formData.questions.length >= 10
                      }
                      className={`button px-6 py-2 rounded-full transition-transform flex items-center justify-center space-x-2 ${
                        selectedStyle !== 'custom' &&
                        formData.questions.length >= 10
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      <span>Add more</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="relative dark:shadow-inner bg-bgPrimary space-y-6 mt-8">
            {/* Custom Questions */}
            {testPermissions.candidate_form_enabled && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isCandidateFormIncluded}
                    onChange={() =>
                      updateFormData({
                        isCandidateFormIncluded:
                          !formData.isCandidateFormIncluded,
                      })
                    }
                    className="w-[15px] h-[15px] rounded text-black-600 focus:ring-black-500 mb-2 accent-blue-500"
                  />
                  <label className="text-sm font-medium dark:text-gray-300 text-textSecondary mb-2">
                    Ask Candidate Details before the test
                  </label>
                </div>
              </div>
            )}
            {(() => {
              const selectedMcqSet = (formData.sets || []).find(
                (set) => set.id === formData.selectedSet,
              );
              const testOptions = [
                {
                  id: 'mcq',
                  isEnabled: testPermissions.mcq_enabled,
                  component: (
                    <div
                      key="mcq"
                      className={`rounded-2xl border transition-all ${!testPermissions.mcq_enabled ? 'opacity-60' : ''} ${formData.isMCQIncluded && testPermissions.mcq_enabled ? 'border-violet-200 dark:border-violet-500/30' : 'border-gray-200 dark:border-white/[0.08]'}`}
                    >
                      <div className="flex items-center gap-3 px-4 py-3.5">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${formData.isMCQIncluded && testPermissions.mcq_enabled ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400' : 'bg-gray-100 dark:bg-white/[0.06] text-gray-400 dark:text-gray-500'}`}>
                          <BookOpen size={17} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Include Knowledge Test</p>
                          {testPermissions.mcq_enabled && formData.isMCQIncluded ? (
                            formData.selectedSet && formData.selectedSet !== 'NA' ? null : (
                              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">Select a set below</p>
                            )
                          ) : (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Test candidates&apos; knowledge with multiple-choice questions</p>
                          )}
                        </div>
                        {!testPermissions.mcq_enabled ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-pink-500/20 to-blue-500/20 border border-pink-500/30 dark:text-pink-300 text-pink-600 flex-shrink-0">
                            ✦ Upgrade Plan
                          </span>
                        ) : (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={formData.isMCQIncluded}
                            aria-label="Include Knowledge Test"
                            onClick={handleIncludeMCQ}
                            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 focus:outline-none ${formData.isMCQIncluded ? 'bg-violet-500' : 'bg-gray-200 dark:bg-white/10'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${formData.isMCQIncluded ? 'translate-x-5' : 'translate-x-0'}`} />
                          </button>
                        )}
                      </div>
                      {testPermissions.mcq_enabled && formData.isMCQIncluded && (
                        <div className="px-4 pb-4">
                          <div className="flex flex-col gap-2 md:max-w-md">
                            <label className="block text-sm font-medium dark:text-gray-300 text-textSecondary">
                              Select a Set
                              <span className="ml-1 text-red-400">*</span>
                            </label>
                            <select
                              value={formData.selectedSet}
                              onChange={(e) =>
                                updateFormData({
                                  selectedSet: e.target.value,
                                  errors: { ...formData.errors, selectedSet: '' },
                                })
                              }
                              className={`dark:text-gray-300 text-textSecondary dark:glass-effect bg-bgPrimary border w-full px-3 py-2.5 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition ${formData.errors?.selectedSet ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                              style={{ height: '43px' }}
                            >
                              <option
                                value=""
                                style={{
                                  background: `${theme === 'dark' ? 'rgba(34, 52, 97, 0.35)' : '#FAFAF9'}`,
                                  color: 'black',
                                }}
                              >
                                Select MCQ Set
                              </option>
                              {(formData.sets || []).map((set) => (
                                <option
                                  key={set.id}
                                  value={set.id}
                                  style={{
                                    background: `${theme === 'dark' ? 'rgba(34, 52, 97, 0.35)' : '#FAFAFA'}`,
                                    color: 'black',
                                  }}
                                >
                                  {set.name}
                                </option>
                              ))}
                            </select>
                            {selectedMcqSet && (
                              <p className="text-xs dark:text-gray-400 text-textSecondary mt-1">
                                {[
                                  selectedMcqSet.question_count
                                    ? `${selectedMcqSet.question_count} questions`
                                    : null,
                                  selectedMcqSet.timelimit
                                    ? `${selectedMcqSet.timelimit} min`
                                    : null,
                                  selectedMcqSet.pass_percent != null
                                    ? `pass at ${selectedMcqSet.pass_percent}%`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  id: 'coding',
                  isEnabled: testPermissions.coding_enabled,
                  component: (
                    <CodingRoundConfig
                      key="coding"
                      isCodingIncluded={formData.isCodingIncluded}
                      selectedCodingSet={formData.selectedCodingSet}
                      codingSets={formData.codingSets || []}
                      codingSetsData={codingSetsData}
                      codingSetProblems={codingSetProblems}
                      selfHostedProblemIds={selfHostedProblemIds}
                      isFetchingSetProblems={isFetchingSetProblems}
                      codingEnabled={testPermissions.coding_enabled}
                      error={formData.errors?.selectedCodingSet}
                      onToggle={handleIncludeCoding}
                      onPreviewSet={fetchCodingSetProblems}
                      onSave={(setName) =>
                        updateFormData({
                          selectedCodingSet: setName,
                          errors: { ...formData.errors, selectedCodingSet: '' },
                        })
                      }
                      onViewProblem={handleViewProblem}
                    />
                  ),
                },
                /* Typing Test and Behavioural Assessment cards hidden for now
                {
                  id: 'typing',
                  isEnabled: testPermissions.typing_enabled,
                  component: (
                    <div
                      key="typing"
                      className={`rounded-2xl border transition-all ${!testPermissions.typing_enabled ? 'opacity-60' : ''} ${formData.isTypingIncluded && testPermissions.typing_enabled ? 'border-violet-200 dark:border-violet-500/30' : 'border-gray-200 dark:border-white/[0.08]'}`}
                    >
                      <div className="flex items-center gap-3 px-4 py-3.5">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${formData.isTypingIncluded && testPermissions.typing_enabled ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400' : 'bg-gray-100 dark:bg-white/[0.06] text-gray-400 dark:text-gray-500'}`}>
                          <Keyboard size={17} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Include Typing Test</p>
                          {testPermissions.typing_enabled && formData.isTypingIncluded ? (
                            Number(formData.typingTimeLimit) > 0 ? (
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-gray-200 dark:border-white/[0.12] bg-gray-50 dark:bg-white/[0.04] text-gray-500 dark:text-gray-400">
                                  <Clock size={9} />
                                  {formData.typingTimeLimit} min
                                </span>
                              </div>
                            ) : (
                              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">Set a time limit below</p>
                            )
                          ) : (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Measure typing speed and accuracy</p>
                          )}
                        </div>
                        {!testPermissions.typing_enabled ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-pink-500/20 to-blue-500/20 border border-pink-500/30 dark:text-pink-300 text-pink-600 flex-shrink-0">
                            ✦ Upgrade Plan
                          </span>
                        ) : (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={formData.isTypingIncluded}
                            aria-label="Include Typing Test"
                            onClick={handleIncludeTyping}
                            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 focus:outline-none ${formData.isTypingIncluded ? 'bg-violet-500' : 'bg-gray-200 dark:bg-white/10'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${formData.isTypingIncluded ? 'translate-x-5' : 'translate-x-0'}`} />
                          </button>
                        )}
                      </div>
                      {testPermissions.typing_enabled && formData.isTypingIncluded && (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 px-4 pb-4">
                          <div className="flex flex-col gap-2">
                            <label className="block text-sm font-medium dark:text-gray-300 text-textSecondary">
                              Time Limit (Minutes)
                              <span className="ml-1 text-red-400">*</span>
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              step="1"
                              value={formData.typingTimeLimit || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow empty or valid number
                                if (
                                  value === '' ||
                                  (/^\d+$/.test(value) && value <= 30)
                                ) {
                                  updateFormData({
                                    typingTimeLimit:
                                      value === '' ? '' : parseInt(value),
                                    errors: {
                                      ...formData.errors,
                                      typingTimeLimit: '',
                                    },
                                  });
                                }
                              }}
                              onBlur={() => {
                                const val = formData.typingTimeLimit;
                                if (!val || val < 1) {
                                  toast.warn(
                                    'Typing test time must be at least 1 minute!',
                                  );
                                  updateFormData({ typingTimeLimit: 1 });
                                } else if (val > 30) {
                                  toast.warn('Typing test cannot exceed 30 minutes!');
                                  updateFormData({ typingTimeLimit: 30 });
                                }
                              }}
                              placeholder="e.g. 3"
                              className={`dark:text-gray-300 text-textSecondary dark:glass-effect bg-bgPrimary border w-full px-3 py-2.5 rounded-lg focus:ring-1 focus:!ring-blue-500 focus:!border-blue-500 transition ${formData.errors?.typingTimeLimit ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                            />
                            <p className="text-xs dark:text-gray-400 text-textSecondary mt-1">
                              Recommended: 1–5 minutes
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  id: 'behavioral',
                  isEnabled: testPermissions.behavioral_enabled,
                  component: (
                    <div
                      key="behavioral"
                      className={`rounded-2xl border transition-all ${!testPermissions.behavioral_enabled ? 'opacity-60' : ''} ${formData.isBehavioralIncluded && testPermissions.behavioral_enabled ? 'border-violet-200 dark:border-violet-500/30' : 'border-gray-200 dark:border-white/[0.08]'}`}
                    >
                      <div className="flex items-center gap-3 px-4 py-3.5">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${formData.isBehavioralIncluded && testPermissions.behavioral_enabled ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400' : 'bg-gray-100 dark:bg-white/[0.06] text-gray-400 dark:text-gray-500'}`}>
                          <Handshake size={17} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Include Behavioural Assessment</p>
                          {testPermissions.behavioral_enabled && formData.isBehavioralIncluded ? (
                            formData.selectedBehavioralSet && formData.selectedBehavioralSet !== 'NA' ? (
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border border-violet-200 dark:border-violet-500/30 bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400">
                                  {(formData.behavioralSets || []).find((set) => set.id === formData.selectedBehavioralSet)?.name || 'Set selected'}
                                </span>
                              </div>
                            ) : (
                              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">Select a set below</p>
                            )
                          ) : (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Assess communication, teamwork, and other core competencies with scenario-based questions</p>
                          )}
                        </div>
                        {!testPermissions.behavioral_enabled ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-pink-500/20 to-blue-500/20 border border-pink-500/30 dark:text-pink-300 text-pink-600 flex-shrink-0">
                            ✦ Upgrade Plan
                          </span>
                        ) : (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={formData.isBehavioralIncluded}
                            aria-label="Include Behavioural Assessment"
                            onClick={handleIncludeBehavioral}
                            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 focus:outline-none ${formData.isBehavioralIncluded ? 'bg-violet-500' : 'bg-gray-200 dark:bg-white/10'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${formData.isBehavioralIncluded ? 'translate-x-5' : 'translate-x-0'}`} />
                          </button>
                        )}
                      </div>
                      {testPermissions.behavioral_enabled && formData.isBehavioralIncluded && (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 px-4 pb-4">
                          <div className="flex flex-col gap-2">
                            <label className="block text-sm font-medium dark:text-gray-300 text-textSecondary">
                              Select a Set
                              <span className="ml-1 text-red-400">*</span>
                            </label>
                            <select
                              value={formData.selectedBehavioralSet}
                              onChange={(e) =>
                                updateFormData({
                                  selectedBehavioralSet: e.target.value,
                                  errors: { ...formData.errors, selectedBehavioralSet: '' },
                                })
                              }
                              className={`dark:text-gray-300 text-textSecondary dark:glass-effect bg-bgPrimary border w-full px-3 py-2.5 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition ${formData.errors?.selectedBehavioralSet ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                              style={{ height: '43px' }}
                            >
                              <option
                                style={{
                                  background: `${theme === 'dark' ? 'rgba(34, 52, 97, 0.35)' : '#FAFAF9'}`,
                                  color: 'black',
                                }}
                              >
                                Select Behavioural Set
                              </option>
                              {(formData.behavioralSets || []).map((set) => (
                                <option
                                  key={set.id}
                                  value={set.id}
                                  style={{
                                    background: `${theme === 'dark' ? 'rgba(34, 52, 97, 0.35)' : '#FAFAFA'}`,
                                    color: 'black',
                                  }}
                                >
                                  {set.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
                */
              ];

              return [...testOptions]
                .sort((a, b) => (b.isEnabled ? 1 : 0) - (a.isEnabled ? 1 : 0))
                .map((test) => test.component);
            })()}

            {/* Assessment Sequence Section */}
            {(testPermissions.mcq_enabled ||
              testPermissions.coding_enabled ||
              testPermissions.typing_enabled ||
              testPermissions.behavioral_enabled) &&
              (formData.isMCQIncluded ||
                formData.isCodingIncluded ||
                formData.isTypingIncluded ||
                formData.isBehavioralIncluded) && (
                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold dark:text-gray-300 text-textSecondary mt-6">
                      Assessment Sequence
                    </h3>
                    <div className="flex items-center text-sm dark:text-gray-300 text-textSecondary mt-6">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      Drag to reorder assessments
                    </div>
                  </div>
                  <div className="space-y-3">
                    {formData.sequence &&
                      formData.sequence.map((item, index) => (
                        <DraggableAssessmentItem
                          key={item.id}
                          id={item.id}
                          index={index}
                          moveItem={moveItem}
                        >
                          <div className="dark:glass-effect bg-bgPrimary dark:text-white  flex items-center space-x-3 p-4 rounded-lg cursor-move  border border-gray-500 dark:hover:border-gray-300 hover:border-gray-400 transition-colors duration-200">
                            <GripVertical
                              className="w-4 h-4 flex-shrink-0 dark:text-gray-400 text-gray-500"
                              aria-hidden="true"
                            />
                            <span
                              className={`flex-1 ${
                                item.mandatory
                                  ? 'font-medium dark:text-pink-400 text-pink-500'
                                  : 'dark:text-gray-300 text-textSecondary'
                              }`}
                            >
                              {item.label}
                            </span>
                            {item.mandatory && (
                              <span className="px-2 py-1 text-xs font-medium text-pink-700 bg-pink-100 rounded-full">
                                Required
                              </span>
                            )}
                          </div>
                        </DraggableAssessmentItem>
                      ))}
                  </div>
                </div>
              )}
          </div>
        </div>
        {!sections.sourcing.isChecked &&
        sections.assessment.isChecked &&
        sections.screening.isChecked ? (
          <div className="mt-6 p-4 bg-red-50 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">
                Invalid combination selected
              </p>
            </div>
          </div>
        ) : (
          <NavigationButtons
            onBack={() => {
              // Reset manual time flag when going back to modify skills
              updateFormData({ isInterviewTimeManuallySet: false });

              if (selectedStyle === 'screening' || selectedStyle === 'custom') {
                setCurrentStep(1);
                setCurrentSubStep(2);
              } else {
                setCurrentStep(2);
                setCurrentSubStep(1);
              }
            }}
            onNext={async () => {
              const isValid = await handleFinalize();
              if (isValid) {
                if (selectedStyle === 'knowledge') {
                  // Saved questions are reused only while they cover exactly the selected
                  // skills — on edit they are always present, so presence alone isn't enough.
                  const rs = formData.generatedJD?.Required_Skills || {};
                  const selectedSkills = new Set([...(rs.Easy || []), ...(rs.Medium || []), ...(rs.Hard || [])]);
                  const questionSkills = Object.keys(formData.Pre_Written_Questions || {});
                  const questionsMatchSkills =
                    questionSkills.length > 0 &&
                    questionSkills.length === selectedSkills.size &&
                    questionSkills.every((skill) => selectedSkills.has(skill));
                  if (questionsMatchSkills) {
                    setCurrentStep(4);
                    setCurrentSubStep(1);
                  } else {
                    const success = await generateSampleQuestions();
                    if (success) {
                      setCurrentStep(4);
                      setCurrentSubStep(1);
                    }
                  }
                } else {
                  setCurrentStep(4);
                  setCurrentSubStep(1);
                }
              }
            }}
            nextDisabled={
              selectedStyle === 'custom' && formData.questions.length === 0
            }
            showborderbtn={true}
          />
        )}
      </div>
    );
  };


  return (
    <DndProvider backend={HTML5Backend}>
      <style>{selectableStyles}</style>
      <div
        className="min-h-screen"
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          willChange: 'auto',
        }}
      >
        {viewMode === 'edit' && (
          <div className="flex flex-col min-h-screen border border-gray-700/50 rounded-2xl pt-4 bg-bgPrimary">
            <div className="flex items-center justify-between px-6 pt-2">
              <div className="flex items-center">
                {currentStep === 1 && currentSubStep === 1 && (
                  <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    title="Back"
                  >
                    <ArrowLeft className="w-5 h-5 cursor-pointer dark:text-[#C9A3FF] text-textSecondary" />
                  </button>
                )}
                <span className="dark:text-gray-400 text-textSecondary">Smart Mode</span>
              </div>
              <button
                onClick={() => setIsHelpVideoOpen(true)}
                className="w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 dark:border-gray-400 border-gray-500 dark:text-gray-400 text-gray-500 text-xs font-semibold hover:opacity-80 transition-opacity"
                title="Help"
              >
                ?
              </button>
            </div>
            {renderProgressBar()}
            <div className="px-4 mt-4 flex-1">
              {currentStep === 1 && currentSubStep === 1 && (
                <SharedStep1_JobDetails
                  formData={formData}
                  setFormData={setFormData}
                  updateFormData={updateFormData}
                  // job role autocomplete
                  jobRoleInputRef={jobRoleInputRef}
                  jobRoleContainerRef={jobRoleContainerRef}
                  suggestionsRef={suggestionsRef}
                  suggestionRefs={suggestionRefs}
                  showJobRoleSuggestions={showJobRoleSuggestions}
                  jobRoleSuggestions={jobRoleSuggestions}
                  selectedSuggestionIndex={selectedSuggestionIndex}
                  handleJobRoleChangeWithSuggestions={
                    handleJobRoleChangeWithSuggestions
                  }
                  handleJobRoleKeyDown={handleJobRoleKeyDown}
                  handleSuggestionSelect={handleSuggestionSelect}
                  // experience
                  handleExperienceSelect={handleExperienceSelect}
                  // jd / file upload
                  fileInputRef={fileInputRef}
                  isDragging={isDragging}
                  isUploadingJD={isUploadingJD}
                  isGeneratingJD={isGeneratingJD}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDropJD={handleDropJD}
                  handleFileInputChange={handleFileInputChange}
                  handleGenerateJDWithAI={handleGenerateJDWithAI}
                  // navigation
                  isStepOneNextDisabled={isStepOneNextDisabled}
                  handleAnalyze={handleAnalyze}
                  onCancel={() => navigate(-1)}
                />
              )}
              {currentStep === 1 && currentSubStep === 2 && renderMagicBox()}
              {currentStep === 2 && (
                <SmartStep2_SelectSkills
                  formData={formData}
                  setFormData={setFormData}
                  updateFormData={updateFormData}
                  handleDrop={handleDrop}
                  deleteItem={deleteItem}
                  theme={theme}
                  customRequiredSkillRef={customRequiredSkillRef}
                  onAddCustomSkill={onAddCustomSkill}
                  selectedStyle={selectedStyle}
                  getDefaultTimeForSkills={getDefaultTimeForSkills}
                  setCurrentStep={setCurrentStep}
                  setCurrentSubStep={setCurrentSubStep}
                  handleGenerateJD={handleGenerateJD}
                />
              )}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && (
                <SharedStep4_Review
                  formData={formData}
                  selectedStyle={selectedStyle}
                  allSkills={[...formData.requiredSkills, ...formData.optionalSkills]}
                  isUploadingJD={isUploadingJD}
                  isJDExpanded={isJDExpanded}
                  setIsJDExpanded={setIsJDExpanded}
                  expandedSections={expandedSections}
                  setExpandedSections={setExpandedSections}
                  editingIndex={editingIndex}
                  setEditingIndex={setEditingIndex}
                  onBack={
                    selectedStyle === 'knowledge'
                      ? () => setCurrentStep(3)
                      : () => { setCurrentStep(3); setCurrentSubStep(2); }
                  }
                  onNext={handleUpdateJD}
                  handleChange={handleChange}
                  handleCommitEdit={handleCommitEdit}
                  getInterviewTypeLabel={getInterviewTypeLabel}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <LimitExceededModal
        isOpen={limitModal.isOpen}
        onClose={() =>
          setLimitModal({ isOpen: false, featureKey: null, errorCode: null })
        }
        featureKey={limitModal.featureKey}
        errorCode={limitModal.errorCode}
      />

      <DiscardChangesModal
        isOpen={isBlocked}
        onCancel={() => reset()}
        onConfirm={() => {
          localStorage.removeItem('createJDFormData');
          localStorage.removeItem('createJDSections');
          proceed();
        }}
      />

      <HelpVideoModal
        open={isHelpVideoOpen}
        onClose={() => setIsHelpVideoOpen(false)}
        videos={HELP_VIDEOS}
        defaultIndex={0}
      />

      {viewingProblem && (
        <ProblemDetailModal
          problem={viewingProblem}
          action="view"
          loading={viewingProblemLoading}
          onClose={() => { setViewingProblem(null); setViewingProblemLoading(false); }}
        />
      )}
    </DndProvider>
  );
}

export default CreateJDSmartView;
